/**
 * Smart Analytics Component
 * 
 * Displays intelligent insights and analysis based on COP data
 * including pattern detection, anomaly alerts, and behavioral analysis.
 * Uses client-side heuristics - no external API calls.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Collapse,
  Badge,
  CircularProgress,
  TextField,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendIcon,
  Warning as WarningIcon,
  LocationOn as LocationIcon,
  Speed as SpeedIcon,
  Groups as GroupsIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  AutoAwesome as InsightIcon,
  Timeline as TimelineIcon,
  DirectionsRun as MovementIcon,
  Psychology as AskIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  CropFree as AreaSelectIcon,
  CheckCircle as CheckIcon,
  Lightbulb as PromptIcon,
  Route as RouteIcon,
  PeopleAlt as CoTravelerIcon,
  PinDrop as DwellIcon,
  Map as MapIcon,
  Assessment as AssessmentIcon,
  HelpOutline as HelpIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import areaIntelService, { type AreaSummary } from '@signals/app-shell/services/areaIntelService';
import deviceDataService from '@signals/app-shell/services/deviceDataService';
import pathfinderApi from '@signals/pathfinder-ui/services/pathfinderApi';
import { copDwellsService } from '../services/copDwellsService';
import { generateSentinelChatPDF } from '@signals/app-shell/components/pdf';

interface DeviceData {
  device_id: string;
  entity_id?: string;
  entity_type?: string;
  device_type?: string;
  latitude?: number;
  longitude?: number;
  last_seen_minutes_ago?: number;
  speed?: number;
  battery_level?: number;
}

interface DwellData {
  id: number;
  entity_id: string;
  device_id: string;
  device_type: string;
  latitude: number;
  longitude: number;
  duration: string;
  duration_seconds: number;
  start_time: string;
  end_time: string;
}

interface AIInsight {
  id: string;
  type: 'pattern' | 'anomaly' | 'alert' | 'insight' | 'summary';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  timestamp: Date;
  icon: React.ReactNode;
  color: string;
  entities?: string[];
  expanded?: boolean;
}

interface MapMarker {
  id: string;
  latitude: number;
  longitude: number;
  label: string;
  type: 'colocation' | 'dwell' | 'poi' | 'alert' | 'custom';
  color?: string;
  description?: string;
  entities?: string[];
}

interface AreaGeometry {
  type: 'Polygon';
  coordinates: number[][][];
}

interface TrackPoint {
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
}

interface EntityRoute {
  entityId: string;
  tracks: TrackPoint[];
  color?: string;
}

interface DwellCircle {
  id: string;
  latitude: number;
  longitude: number;
  radiusMeters: number;
  entityId: string;
  deviceId?: string;
  durationSeconds: number;
  visitCount?: number;
  startTime?: string;
  endTime?: string;
}

interface MapAction {
  type: 'zoom_to_entity' | 'zoom_to_location' | 'zoom_to_bounds' | 'filter_entity_type' | 'add_markers' | 'clear_markers' | 'start_area_analysis' | 'cancel_area_analysis' | 'show_entity_route' | 'clear_routes' | 'show_entity_dwells' | 'clear_dwells' | 'clear_all_ai';
  entityId?: string;
  latitude?: number;
  longitude?: number;
  zoom?: number;
  entityType?: string;
  markers?: MapMarker[];
  routes?: EntityRoute[];
  dwellCircles?: DwellCircle[];
}

interface GeofenceData {
  id: number;
  name: string;
  description?: string;
  type?: string;
  is_active?: boolean;
  device_count?: number;
  alert_count?: number;
  geometry?: {
    type: string;
    coordinates: number[][][];
  };
}

interface SmartAnalyticsProps {
  devices: DeviceData[];
  totalDevices: number;
  activeDevices: number;
  lastUpdated: Date | null;
  sseConnected?: boolean;
  filters?: {
    course?: string;
    entityType?: string;
    entity?: string;
  };
  currentTab?: string;
  devicesInView?: number;
  dwells?: DwellData[];
  geofences?: GeofenceData[];
  onMapAction?: (action: MapAction) => void;
  mapViewport?: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  // Area analysis - drawn polygon from map
  analysisArea?: AreaGeometry | null;
  isDrawingAnalysisArea?: boolean;
}

const SmartAnalytics = ({
  devices,
  totalDevices,
  activeDevices,
  lastUpdated,
  sseConnected = false,
  filters = {},
  currentTab = 'Events',
  devicesInView = 0,
  dwells = [],
  geofences = [],
  onMapAction,
  mapViewport,
  analysisArea,
  isDrawingAnalysisArea = false,
}: SmartAnalyticsProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  
  // Quick Ask state
  const [quickAskLoading, setQuickAskLoading] = useState(false);
  const [quickAskResponse, setQuickAskResponse] = useState<string | null>(null);
  const [quickAskQuestion, setQuickAskQuestion] = useState('');
  const [quickAskSuggestions, setQuickAskSuggestions] = useState<string[]>([]);
  
  // Chat mode - when user is actively engaging with Ask Sentinel
  const [isChatMode, setIsChatMode] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  // Prompt library menu state
  const [promptMenuAnchor, setPromptMenuAnchor] = useState<null | HTMLElement>(null);
  const promptMenuOpen = Boolean(promptMenuAnchor);
  
  // Predefined analysis prompts
  const predefinedPrompts = [
    { category: 'How to Use', icon: <HelpIcon fontSize="small" />, prompts: [
      { label: 'What can you help with?', prompt: 'What can you help me with on the Dashboard?' },
      { label: 'How to track entities', prompt: 'How do I track an entity and view their route on the map?' },
      { label: 'How to find co-travelers', prompt: 'How do I find co-travelers or entities traveling together?' },
      { label: 'How to use area analysis', prompt: 'How do I draw an area on the map and analyze activity within it?' },
      { label: 'Understanding the map', prompt: 'Explain the different markers and layers on the map' },
      { label: 'How to view dwells', prompt: 'How do I see where an entity has stopped or dwelled?' },
    ]},
    { category: 'Status', icon: <AssessmentIcon fontSize="small" />, prompts: [
      { label: 'Fleet Status Summary', prompt: 'Give me a summary of the current fleet status' },
      { label: 'Device Connectivity', prompt: 'Which devices have connectivity issues?' },
      { label: 'Active Movement', prompt: 'Which entities are currently moving?' },
    ]},
    { category: 'Location Analysis', icon: <DwellIcon fontSize="small" />, prompts: [
      { label: 'Top Dwell Locations', prompt: 'What are the top dwell locations across all entities?' },
      { label: 'Extended Stops', prompt: 'Show me any extended stops over 2 hours' },
      { label: 'Geofence Activity', prompt: 'What is the current geofence activity?' },
    ]},
    { category: 'Entity Analysis', icon: <RouteIcon fontSize="small" />, prompts: [
      { label: 'Show Entity Route', prompt: 'Show route for ' },
      { label: 'Roads Traveled', prompt: 'What roads did ' },
      { label: 'Entity Dwells on Map', prompt: 'Load dwells for ' },
      { label: 'Entity Status', prompt: 'What is the status of ' },
    ]},
    { category: 'Co-Travel & Patterns', icon: <CoTravelerIcon fontSize="small" />, prompts: [
      { label: 'Co-Traveler Scan', prompt: 'Are there any co-travelers detected?' },
      { label: 'Co-Location Events', prompt: 'Show co-location events on the map' },
      { label: 'Movement Patterns', prompt: 'What movement patterns have been detected?' },
    ]},
    { category: 'Map Actions', icon: <MapIcon fontSize="small" />, prompts: [
      { label: 'Clear Map', prompt: 'Clear the map' },
      { label: 'Draw Analysis Area', prompt: 'Start area analysis' },
      { label: 'Clear Analysis Area', prompt: 'Clear area' },
      { label: 'Show All Markers', prompt: 'Show all dwell locations on map' },
    ]},
  ];
  
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [_lastAnalysis, setLastAnalysis] = useState<Date | null>(null);
  const [seenInsightIds, setSeenInsightIds] = useState<Set<string>>(new Set());

  // Helper to convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '46, 134, 171';
  };

  const primaryRgb = hexToRgb(primaryColor);

  // Point-in-polygon algorithm (ray casting)
  const isPointInPolygon = (point: [number, number], polygon: number[][]): boolean => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      const intersect = ((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      
      if (intersect) inside = !inside;
    }
    
    return inside;
  };

  // Filter dwells within the analysis area
  const filterDwellsInArea = (area: AreaGeometry | null | undefined): DwellData[] => {
    if (!area || !area.coordinates || area.coordinates.length === 0) return dwells;
    
    const polygon = area.coordinates[0]; // First ring of the polygon
    
    return dwells.filter(dwell => {
      if (!dwell.latitude || !dwell.longitude) return false;
      return isPointInPolygon([dwell.longitude, dwell.latitude], polygon);
    });
  };

  // Filter devices within the analysis area
  const filterDevicesInArea = (area: AreaGeometry | null | undefined): DeviceData[] => {
    if (!area || !area.coordinates || area.coordinates.length === 0) return devices;
    
    const polygon = area.coordinates[0];
    
    return devices.filter(device => {
      if (!device.latitude || !device.longitude) return false;
      return isPointInPolygon([device.longitude, device.latitude], polygon);
    });
  };

  // Calculate area bounds for display
  const getAreaBounds = (area: AreaGeometry | null | undefined): { minLat: number; maxLat: number; minLon: number; maxLon: number } | null => {
    if (!area || !area.coordinates || area.coordinates.length === 0) return null;
    
    const polygon = area.coordinates[0];
    const lats = polygon.map(p => p[1]);
    const lons = polygon.map(p => p[0]);
    
    return {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLon: Math.min(...lons),
      maxLon: Math.max(...lons),
    };
  };

  // Detect and execute map actions from user question
  const detectAndExecuteMapAction = (question: string): { 
    executed: boolean; 
    message?: string; 
    latitude?: number; 
    longitude?: number;
    requestDwells?: boolean;
    dwellEntitySearch?: string;
    requestRoute?: boolean;
    requestRoads?: boolean;
    routeEntitySearch?: string;
  } => {
    if (!onMapAction) return { executed: false };
    
    const q = question.toLowerCase().trim();
    console.log('[detectAndExecuteMapAction] Processing query:', q);
    
    // Zoom to specific entity patterns
    const zoomToEntityPatterns = [
      /(?:zoom|go|fly|pan|show|find|locate|center)\s+(?:to|on|at)?\s*(?:entity\s+)?["']?([a-zA-Z0-9_-]+)["']?/i,
      /(?:where\s+is|show\s+me|find)\s+(?:entity\s+)?["']?([a-zA-Z0-9_-]+)["']?/i,
      /(?:zoom|center|focus)\s+(?:on\s+)?["']?([a-zA-Z0-9_-]+)["']?\s+(?:on\s+)?(?:the\s+)?map/i,
    ];
    
    // Words that should NOT be treated as entity names (analysis terms, common words)
    const nonEntityTerms = [
      'co-location', 'colocation', 'co-traveler', 'cotraveler', 'co-travel',
      'dwell', 'dwells', 'route', 'routes', 'track', 'tracks', 'marker', 'markers',
      'map', 'area', 'analysis', 'insights', 'status', 'fleet', 'all', 'any',
      'location', 'locations', 'event', 'events', 'pattern', 'patterns',
      'geofence', 'geofences', 'alert', 'alerts', 'device', 'devices',
    ];
    
    for (const pattern of zoomToEntityPatterns) {
      const match = q.match(pattern);
      if (match) {
        const searchTerm = match[1].toLowerCase();
        
        // Skip if this is a common analysis term, not an entity
        if (nonEntityTerms.includes(searchTerm.replace(/[-_]/g, ''))) {
          console.log('[Quick Ask] Skipping non-entity term:', searchTerm);
          continue;
        }
        
        // Normalize: remove hyphens, underscores, spaces for fuzzy matching
        const normalizedSearch = searchTerm.replace(/[-_\s]/g, '');
        console.log('[Quick Ask] Looking for entity:', searchTerm, '(normalized:', normalizedSearch, ') in', devices.length, 'devices');
        
        // Normalize function for comparison
        const normalize = (str: string) => str?.toLowerCase().replace(/[-_\s]/g, '') || '';
        
        // Find the device/entity - try exact match first
        let device = devices.find(d => 
          d.entity_id?.toLowerCase() === searchTerm ||
          d.device_id?.toLowerCase() === searchTerm
        );
        
        // If no exact match, try normalized match
        if (!device) {
          device = devices.find(d => 
            normalize(d.entity_id || '') === normalizedSearch ||
            normalize(d.device_id || '') === normalizedSearch
          );
        }
        
        // If still no match, try partial/contains match
        if (!device) {
          device = devices.find(d => 
            normalize(d.entity_id || '').includes(normalizedSearch) ||
            normalize(d.device_id || '').includes(normalizedSearch) ||
            normalizedSearch.includes(normalize(d.entity_id || '')) ||
            normalizedSearch.includes(normalize(d.device_id || ''))
          );
        }
        
        // Last resort: find any entity that starts with the search term
        if (!device) {
          device = devices.find(d => 
            normalize(d.entity_id || '').startsWith(normalizedSearch.substring(0, 3)) ||
            normalize(d.device_id || '').startsWith(normalizedSearch.substring(0, 3))
          );
        }
        
        // Debug: show some sample entity IDs if not found
        if (!device && devices.length > 0) {
          const sampleEntities = devices.slice(0, 5).map(d => d.entity_id).join(', ');
          console.log('[Quick Ask] Sample entity IDs in data:', sampleEntities);
        }
        
        if (device && device.latitude && device.longitude) {
          console.log('[Quick Ask] Found device with coordinates:', device.entity_id, device.latitude, device.longitude);
onMapAction({
            type: 'zoom_to_entity',
            entityId: device.entity_id || device.device_id,
            latitude: device.latitude,
            longitude: device.longitude,
            zoom: 16,
          });
          return {
            executed: true,
            message: `**Map Updated!** Zoomed to **${device.entity_id || device.device_id}** at (${device.latitude.toFixed(4)}, ${device.longitude.toFixed(4)})`,
            latitude: device.latitude,
            longitude: device.longitude,
          };
        } else if (device) {
          console.log('[Quick Ask] Found device but no coordinates:', device.entity_id);
          return { 
            executed: false, 
            message: `Found **${device.entity_id || device.device_id}** but no location data available. Device may be offline.`
          };
        } else {
          console.log('[Quick Ask] Entity not found:', searchTerm);
          // Check if it's in dwells
          const dwellMatch = dwells.find(d => 
            d.entity_id?.toLowerCase().includes(searchTerm)
          );
          if (dwellMatch && dwellMatch.latitude && dwellMatch.longitude) {
            onMapAction({
              type: 'zoom_to_location',
              latitude: dwellMatch.latitude,
              longitude: dwellMatch.longitude,
              zoom: 16,
            });
            return {
              executed: true,
              message: `**Map Updated!** Zoomed to dwell location for **${dwellMatch.entity_id}** at (${dwellMatch.latitude.toFixed(4)}, ${dwellMatch.longitude.toFixed(4)})`,
              latitude: dwellMatch.latitude,
              longitude: dwellMatch.longitude,
            };
          }
          return {
            executed: false,
            message: `Entity "${searchTerm}" not found in current view. Try a different search term or check the entity list.`
          };
        }
      }
    }
    
    // Filter by entity type patterns
    const entityTypePatterns = [
      /(?:show|filter|display|zoom\s+to)\s+(?:all\s+)?(?:the\s+)?(students?|rp|srp)(?:\s+on\s+(?:the\s+)?map)?/i,
      /(?:where\s+are|find)\s+(?:all\s+)?(?:the\s+)?(students?|rp|srp)/i,
    ];
    
    for (const pattern of entityTypePatterns) {
      const match = q.match(pattern);
      if (match) {
        const typeRaw = match[1].toLowerCase();
        const entityType = typeRaw === 'students' || typeRaw === 'student' ? 'Student' :
                          typeRaw === 'rp' ? 'RP' :
                          typeRaw === 'srp' ? 'SRP' : null;
        
        if (entityType) {
          // Find all devices of this type with locations
          const typeDevices = devices.filter(d => 
            d.entity_type === entityType && d.latitude && d.longitude
          );
          
          if (typeDevices.length > 0) {
            // Calculate bounds center
            const lats = typeDevices.map(d => d.latitude!);
            const lngs = typeDevices.map(d => d.longitude!);
            const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
            const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
            
onMapAction({
              type: 'filter_entity_type',
              entityType,
              latitude: centerLat,
              longitude: centerLng,
              zoom: 12,
            });
            return {
              executed: true,
              message: `**Map Updated!** Showing ${typeDevices.length} ${entityType} entities on the map.`,
              latitude: centerLat,
              longitude: centerLng,
            };
          }
        }
      }
    }
    
    // Zoom out / show all patterns
    if (/(?:show\s+all|zoom\s+out|reset\s+map|overview|full\s+view)/i.test(q)) {
      const devicesWithLocation = devices.filter(d => d.latitude && d.longitude);
      if (devicesWithLocation.length > 0) {
        const lats = devicesWithLocation.map(d => d.latitude!);
        const lngs = devicesWithLocation.map(d => d.longitude!);
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
        
onMapAction({
          type: 'zoom_to_bounds',
          latitude: centerLat,
          longitude: centerLng,
          zoom: 10,
        });
        return {
          executed: true,
          message: `**Map Updated!** Showing overview of all ${devicesWithLocation.length} devices.`,
          latitude: centerLat,
          longitude: centerLng,
        };
      }
    }
    
    // Clear ALL AI elements from map (markers, routes, dwells)
    if (/(?:clear|reset|clean)\s+(?:the\s+)?(?:map|everything|all)|(?:remove|hide|delete)\s+(?:all\s+)?(?:ai|everything)\s*(?:from\s+)?(?:the\s+)?(?:map)?/i.test(q)) {
      onMapAction({ type: 'clear_all_ai' });
      return {
        executed: true,
        message: `**Map Cleared!** Removed all AI-added elements (markers, routes, dwell circles) from the map.`,
      };
    }

    // Clear markers pattern
    if (/(?:clear|remove|hide|delete)\s+(?:all\s+)?(?:the\s+)?markers?/i.test(q)) {
      onMapAction({ type: 'clear_markers' });
      return {
        executed: true,
        message: `**Map Updated!** Cleared all markers from the map.`,
      };
    }

    // Clear selected analysis area
    if (/(?:clear|remove|hide|delete|cancel)\s+(?:the\s+)?(?:selected\s+)?(?:analysis\s+)?area/i.test(q)) {
      onMapAction({ type: 'cancel_area_analysis' });
      return {
        executed: true,
        message: `**Area Cleared!** Removed the selected analysis area from the map.`,
      };
    }
    
    // Show co-locations on map
    if (/(?:show|mark|display|plot)\s+(?:the\s+)?(?:co-?locations?|meetings?|encounters?)\s+(?:on\s+)?(?:the\s+)?(?:map)?/i.test(q)) {
      // We'll build co-location markers - need to access the data
      // This will be handled after co-location detection runs
      return { executed: false, requestCoLocationMarkers: true } as any;
    }
    
    // Show dwell locations on map for a specific entity
    const dwellMarkerPattern = /(?:show|mark|display|plot)\s+(?:all\s+)?(?:the\s+)?(?:dwell|stop)s?\s+(?:for\s+)?["']?([a-zA-Z0-9_-]+)["']?\s*(?:on\s+)?(?:the\s+)?(?:map)?/i;
    const dwellMarkerMatch = q.match(dwellMarkerPattern);
    if (dwellMarkerMatch) {
      const entitySearch = dwellMarkerMatch[1].toLowerCase();
      const entityDwells = dwells.filter(d => 
        d.entity_id?.toLowerCase().includes(entitySearch)
      );
      
      if (entityDwells.length > 0) {
        // Create markers for each unique dwell location
        const uniqueLocations = new Map<string, { lat: number; lon: number; count: number; totalSeconds: number }>();
        
        entityDwells.forEach(d => {
          if (d.latitude && d.longitude) {
            const key = `${d.latitude.toFixed(3)},${d.longitude.toFixed(3)}`;
            const existing = uniqueLocations.get(key);
            if (existing) {
              existing.count++;
              existing.totalSeconds += d.duration_seconds || 0;
            } else {
              uniqueLocations.set(key, { lat: d.latitude, lon: d.longitude, count: 1, totalSeconds: d.duration_seconds || 0 });
            }
          }
        });
        
        const markers: MapMarker[] = Array.from(uniqueLocations.entries()).map(([key, loc], idx) => {
          const hours = Math.floor(loc.totalSeconds / 3600);
          const mins = Math.floor((loc.totalSeconds % 3600) / 60);
          const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
          
          return {
            id: `dwell-${key}`,
            latitude: loc.lat,
            longitude: loc.lon,
            label: `Stop ${idx + 1}`,
            type: 'dwell' as const,
            color: '#FF9800',
            description: `${loc.count} visit(s), Total: ${timeStr}`,
            entities: [entityDwells[0].entity_id],
          };
        });
        
        // Calculate center
        const lats = markers.map(m => m.latitude);
        const lons = markers.map(m => m.longitude);
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
        
        onMapAction({
          type: 'add_markers',
          markers,
          latitude: centerLat,
          longitude: centerLon,
          zoom: 12,
        });
        
        return {
          executed: true,
          message: `**Map Updated!** Added **${markers.length} dwell markers** for **${entityDwells[0].entity_id}** on the map.`,
          latitude: centerLat,
          longitude: centerLon,
        };
      }
    }
    
    // Show top dwell locations on map (general)
    if (/(?:show|mark|display|plot)\s+(?:all\s+)?(?:the\s+)?(?:top\s+)?(?:dwell|stop)\s*(?:locations?|spots?|points?)?\s*(?:on\s+)?(?:the\s+)?(?:map)?/i.test(q)) {
      if (dwells.length > 0) {
        // Get unique locations with visit counts
        const uniqueLocations = new Map<string, { lat: number; lon: number; count: number; totalSeconds: number; entities: Set<string> }>();
        
        dwells.forEach(d => {
          if (d.latitude && d.longitude) {
            const key = `${d.latitude.toFixed(3)},${d.longitude.toFixed(3)}`;
            const existing = uniqueLocations.get(key);
            if (existing) {
              existing.count++;
              existing.totalSeconds += d.duration_seconds || 0;
              existing.entities.add(d.entity_id);
            } else {
              uniqueLocations.set(key, { 
                lat: d.latitude, 
                lon: d.longitude, 
                count: 1, 
                totalSeconds: d.duration_seconds || 0,
                entities: new Set([d.entity_id])
              });
            }
          }
        });
        
        // Sort by total time and take top 10
        const topLocations = Array.from(uniqueLocations.entries())
          .sort((a, b) => b[1].totalSeconds - a[1].totalSeconds)
          .slice(0, 10);
        
        const markers: MapMarker[] = topLocations.map(([key, loc], idx) => {
          const hours = Math.floor(loc.totalSeconds / 3600);
          const mins = Math.floor((loc.totalSeconds % 3600) / 60);
          const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
          
          return {
            id: `dwell-${key}`,
            latitude: loc.lat,
            longitude: loc.lon,
            label: `#${idx + 1}`,
            type: 'dwell' as const,
            color: loc.entities.size > 1 ? '#f44336' : '#FF9800', // Red if multiple entities
            description: `${loc.count} visit(s) by ${loc.entities.size} entity/entities, Total: ${timeStr}`,
            entities: Array.from(loc.entities),
          };
        });
        
        // Calculate center
        const lats = markers.map(m => m.latitude);
        const lons = markers.map(m => m.longitude);
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
        
        onMapAction({
          type: 'add_markers',
          markers,
          latitude: centerLat,
          longitude: centerLon,
          zoom: 11,
        });
        
        return {
          executed: true,
          message: `**Map Updated!** Added **${markers.length} top dwell locations** on the map (sorted by total time spent).`,
          latitude: centerLat,
          longitude: centerLon,
        };
      }
    }
    
    // Clear routes pattern
    if (/(?:clear|remove|hide|delete)\s+(?:all\s+)?(?:the\s+)?(?:route|track|trail|path)s?/i.test(q)) {
      onMapAction({ type: 'clear_routes' });
      return {
        executed: true,
        message: `**Map Updated!** Cleared all routes from the map.`,
      };
    }

    // Clear dwells pattern
    if (/(?:clear|remove|hide|delete)\s+(?:all\s+)?(?:the\s+)?(?:dwell|stop)s?\s*(?:circles?|markers?)?/i.test(q)) {
      onMapAction({ type: 'clear_dwells' });
      return {
        executed: true,
        message: `**Map Updated!** Cleared all dwell circles from the map.`,
      };
    }

    // Common words to exclude from entity matching
    const excludedWords = ['me', 'the', 'a', 'an', 'to', 'on', 'in', 'for', 'of', 'data', 'map', 'show', 'display'];

    // Show dwells on map for entity - detect pattern and return flag for async handling
    const dwellDisplayPatterns = [
      // "show dwells for QP1-Test" or "display stops for Student-12"
      /(?:show|display|plot|draw|load)\s+(?:the\s+)?(?:dwell|stop|stationary)s?\s+(?:locations?\s+)?(?:for|of)\s+["']?([a-zA-Z][a-zA-Z0-9]*[-_][a-zA-Z0-9_-]+)["']?/i,
      // "QP1-Test's dwells" or "QP1-Test dwell locations" (entity with hyphen/underscore)
      /["']?([a-zA-Z][a-zA-Z0-9]*[-_][a-zA-Z0-9_-]+)["']?(?:'?s)?\s+(?:dwell|stop)s?\s*(?:locations?)?/i,
      // "load all QP1-Test dwell locations" - entity before dwell keyword
      /(?:load|show|display)\s+(?:all\s+)?["']?([a-zA-Z][a-zA-Z0-9]*[-_][a-zA-Z0-9_-]+)["']?\s+(?:dwell|stop)s?\s*(?:locations?)?/i,
      // "where did QP1-Test stop" or "where has QP1-Test stayed"
      /(?:where\s+(?:did|has))\s+["']?([a-zA-Z][a-zA-Z0-9]*[-_]?[a-zA-Z0-9_-]+)["']?\s+(?:stop|stay|dwell|park)/i,
      // "load dwells for QP1-Test on map"
      /(?:load|show|display)\s+(?:the\s+)?(?:dwell|stop)s?\s+(?:for\s+)?["']?([a-zA-Z][a-zA-Z0-9]*[-_][a-zA-Z0-9_-]+)["']?\s*(?:on\s+)?(?:the\s+)?(?:map)?/i,
    ];

    for (const pattern of dwellDisplayPatterns) {
      const match = q.match(pattern);
      if (match) {
        const entitySearch = match[1].toLowerCase();
        console.log(`[Quick Map Action] Dwell pattern matched: "${entitySearch}"`);
        if (excludedWords.includes(entitySearch)) continue;
        return {
          executed: false,
          requestDwells: true,
          dwellEntitySearch: entitySearch,
        };
      }
    }

    // Fallback: If dwell keywords are present, scan for known entity IDs
    const dwellKeywords = /(?:dwell|stop|stationary)\s*(?:location|point|circle|marker|on\s+map)/i;
    if (dwellKeywords.test(q)) {
      console.log('[Quick Map Action] Dwell keyword detected, scanning for entity IDs...');
      const uniqueEntityIds = Array.from(new Set([
        ...devices.map(d => d.entity_id).filter(Boolean),
        ...dwells.map(d => d.entity_id).filter(Boolean)
      ]));
      uniqueEntityIds.sort((a, b) => (b?.length || 0) - (a?.length || 0));
      
      for (const entityId of uniqueEntityIds) {
        if (!entityId) continue;
        const escapedId = entityId.replace(/[-_]/g, '[-_\\s]?');
        const entityPattern = new RegExp(`\\b${escapedId}\\b`, 'i');
        if (entityPattern.test(q)) {
          console.log(`[Quick Map Action] Found entity "${entityId}" via fallback scan for dwells`);
          return {
            executed: false,
            requestDwells: true,
            dwellEntitySearch: entityId.toLowerCase(),
          };
        }
      }
    }

    // Road lookup patterns - "what roads did X travel on", "roads traveled by X"
    const roadPatterns = [
      // "what roads did QP1-Test travel on/take"
      /(?:what|which)\s+(?:roads?|streets?)\s+(?:did|has)\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?\s+(?:travel|take|drive|use|go)/i,
      // "roads traveled by QP1-Test"
      /(?:roads?|streets?)\s+(?:traveled|taken|used|driven)\s+(?:by|for)\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?/i,
      // "QP1-Test's roads" or "roads of QP1-Test"
      /["']?([a-zA-Z]+[-_][a-zA-Z0-9_-]+)["']?(?:'?s)?\s+(?:roads?|streets?)/i,
      // "show roads for QP1-Test"
      /(?:show|list|display|get)\s+(?:the\s+)?(?:roads?|streets?)\s+(?:for|of)\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?/i,
      // "what route did X take" (wants road names, not just the track)
      /(?:what|which)\s+(?:route|path)\s+(?:did|has)\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?\s+(?:take|travel|drive|consist)/i,
    ];
    
    for (const pattern of roadPatterns) {
      const match = q.match(pattern);
      if (match && match[1]) {
        const entitySearch = match[1].toLowerCase();
        if (excludedWords.includes(entitySearch)) continue;
        // Return flag for road lookup (includes route display + road names)
        return { 
          executed: false, 
          requestRoute: true,
          requestRoads: true,
          routeEntitySearch: entitySearch,
        };
      }
    }
    
    // Generic road query patterns (no entity captured - will use fallback scanner)
    // "what roads did his/her/their route consist of"
    if (/(?:his|her|their|the)\s+route\s+consist/i.test(q) ||
        /(?:what|which)\s+(?:roads?|streets?)\s+(?:were|are)\s+(?:on\s+)?(?:the\s+)?route/i.test(q)) {
      // Flag for road lookup, entity will be found by fallback scanner
      return { 
        executed: false, 
        requestRoute: true,
        requestRoads: true,
      };
    }
    
    // Combined route AND dwell patterns - detect when user wants both
    // "display QP1-Test route and dwell activity" or "show QP1-Test historic route and dwells"
    const combinedRouteAndDwellPatterns = [
      /(?:display|show|load)\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?\s+(?:historic\s+)?(?:route|track)\s+(?:and|&)\s+(?:dwell|stop)/i,
      /(?:display|show|load)\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?\s+(?:dwell|stop)s?\s+(?:and|&)\s+(?:route|track)/i,
      /["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?(?:'?s)?\s+(?:historic\s+)?(?:route|track)\s+(?:and|&)\s+(?:dwell|stop)/i,
      /["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?(?:'?s)?\s+(?:dwell|stop)s?\s+(?:and|&)\s+(?:route|track)/i,
    ];
    
    for (const pattern of combinedRouteAndDwellPatterns) {
      const match = q.match(pattern);
      if (match && match[1]) {
        const entitySearch = match[1].toLowerCase();
        if (excludedWords.includes(entitySearch)) continue;
        console.log(`[Quick Map Action] Combined route+dwell pattern matched for: "${entitySearch}"`);
        return { 
          executed: false, 
          requestRoute: true,
          requestRoads: true, // Also get road names
          routeEntitySearch: entitySearch,
          requestDwells: true,
          dwellEntitySearch: entitySearch,
        };
      }
    }

    // Show route/track for entity - this will be handled async in handleQuickAsk
    // More specific patterns to avoid matching common words like "me", "the", "data"
    const routePatterns = [
      // "show route for QP1-Test" or "display track for Student-12"
      /(?:show|display|plot|draw|trace)\s+(?:the\s+)?(?:route|track|trail|path|movement|history)\s+(?:for|of)\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?/i,
      // "where has QP1-Test been" or "where did Student-12 go"
      /(?:where\s+(?:has|did))\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?\s+(?:go|gone|been|travel|move)/i,
      // "QP1-Test's route" or "QP1-Test route" - entity must have hyphen/underscore or be multi-part
      /["']?([a-zA-Z]+[-_][a-zA-Z0-9_-]+)["']?(?:'?s)?\s+(?:route|track|trail|path|movement|history)/i,
      // "show QP1-Test on map" with route/track context
      /(?:show|display)\s+(?:me\s+)?["']?([a-zA-Z]+[-_][a-zA-Z0-9_-]+)["']?\s+(?:route|track|on\s+(?:the\s+)?map)/i,
    ];
    
    for (const pattern of routePatterns) {
      const match = q.match(pattern);
      if (match) {
        const entitySearch = match[1].toLowerCase();
        // Skip if it's a common word
        if (excludedWords.includes(entitySearch)) continue;
        
        // Check if query also mentions dwells - if so, request both
        const alsoDwells = /dwell|stop|stay/i.test(q);
        
        return { 
          executed: false, 
          requestRoute: true,
          routeEntitySearch: entitySearch,
          requestDwells: alsoDwells,
          dwellEntitySearch: alsoDwells ? entitySearch : undefined,
        };
      }
    }
    
    // Fallback: If both route AND dwell keywords exist, try to find entity via known entities
    const hasRouteKeyword = /route|track|trail|path|movement|history|travel/i.test(q);
    const hasDwellKeyword = /dwell|stop|stay|visit/i.test(q);
    
    if (hasRouteKeyword && hasDwellKeyword) {
      // Look for known entity IDs in the query
      const allKnownEntityIds = [
        ...devices.map(d => d.entity_id).filter(Boolean),
        ...dwells.map(d => d.entity_id).filter(Boolean),
      ];
      const uniqueEntityIds = Array.from(new Set(allKnownEntityIds));
      
      for (const entityId of uniqueEntityIds) {
        if (!entityId || excludedWords.includes(entityId.toLowerCase())) continue;
        const escapedId = entityId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const entityPattern = new RegExp(`\\b${escapedId}\\b`, 'i');
        if (entityPattern.test(q)) {
          console.log(`[Quick Map Action] Combined route+dwell fallback found entity "${entityId}"`);
          return {
            executed: false,
            requestRoute: true,
            requestRoads: true,
            routeEntitySearch: entityId.toLowerCase(),
            requestDwells: true,
            dwellEntitySearch: entityId.toLowerCase(),
          };
        }
      }
    }
    
    return { executed: false };
  };
  
  // Helper to add co-location markers (called after co-location detection)
  const addCoLocationMarkers = (coLocations: any[]): { executed: boolean; message?: string } => {
    if (!onMapAction || coLocations.length === 0) {
      return { executed: false, message: 'No co-location events to display.' };
    }
    
    const markers: MapMarker[] = coLocations.slice(0, 10).map((event, idx) => {
      const loc = event.location || {};
      const entities = event.entities || [];
      const uniqueEntityIds: string[] = Array.from(new Set(entities.map((e: any) => e.entityId as string)));
      const overlapMins = event.overlapMinutes || 0;
      
      const timeStr = overlapMins >= 60 
        ? `${Math.floor(overlapMins / 60)}h ${overlapMins % 60}m` 
        : `${overlapMins}m`;
      
      return {
        id: `coloc-${idx}`,
        latitude: loc.latitude || 0,
        longitude: loc.longitude || 0,
        label: `Meet ${idx + 1}`,
        type: 'colocation' as const,
        color: overlapMins >= 30 ? '#f44336' : overlapMins >= 10 ? '#FF9800' : '#4CAF50',
        description: `${uniqueEntityIds.join(' & ')} - ${timeStr} overlap`,
        entities: uniqueEntityIds,
      };
    });
    
    if (markers.length === 0) {
      return { executed: false, message: 'No co-location events with valid locations.' };
    }
    
    // Calculate center
    const lats = markers.map(m => m.latitude);
    const lons = markers.map(m => m.longitude);
    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
    
    onMapAction({
      type: 'add_markers',
      markers,
      latitude: centerLat,
      longitude: centerLon,
      zoom: 11,
    });
    
    return {
      executed: true,
      message: `**Map Updated!** Added **${markers.length} co-location markers** on the map.\n- Red: Extended meetings (30+ min)\n- Orange: Brief encounters (10-30 min)\n- Green: Short overlaps (<10 min)`,
    };
  };

  // Quick Ask API call
  const handleQuickAsk = async (question?: string) => {
    const queryText = question || quickAskQuestion || '';
    if (!queryText.trim()) return;
    
    setQuickAskLoading(true);
    setQuickAskResponse(null);
    setIsChatMode(true); // Enter chat mode when user asks a question
    
    // Add user message to chat history
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: queryText,
      timestamp: new Date(),
    }]);
    
    // Clear input after sending
    setQuickAskQuestion('');
    
// First, check for map actions
    const mapAction = detectAndExecuteMapAction(queryText);
    let mapActionPrefix = '';
    let locationContext: any = null;
    
    if (mapAction.executed && mapAction.message) {
      // Map action was executed, show confirmation and still get AI response
      mapActionPrefix = mapAction.message + '\n\n---\n\n';
      setQuickAskResponse(mapAction.message + '\n\n_Fetching location context..._');
      
      // Fetch location context for the zoomed location
      if (mapAction.latitude && mapAction.longitude) {
        try {
          locationContext = await areaIntelService.getLocationContext(
            mapAction.latitude, 
            mapAction.longitude
          );
          console.log('[Quick Ask] Location context:', locationContext);
        } catch (e) {
          console.log('[Quick Ask] Could not fetch location context:', e);
        }
      }
    } else if (!mapAction.executed && mapAction.message) {
      // Map action was attempted but couldn't complete (e.g., no coordinates)
      mapActionPrefix = mapAction.message + '\n\n---\n\n';
    }
    
    // Handle route request - fetch track data from database
    let routeData: EntityRoute[] | null = null;
    let entitySearch = mapAction.routeEntitySearch;
    
    // Check if user is asking about roads (for later road lookup)
    const roadKeywords = /road|street|consist/i;
    const isRoadQuery = roadKeywords.test(queryText);
    
    // Fallback: If patterns didn't capture an entity but query mentions route/track/road, 
    // scan query for known entity IDs
    const routeKeywords = /route|track|trail|path|movement|history|traveled|went|been|road|street|consist/i;
    if (!entitySearch && routeKeywords.test(queryText) && onMapAction) {
      console.log('[Quick Ask] Route/road keyword detected, scanning for entity IDs...');
      const uniqueEntityIds = Array.from(new Set(devices.map(d => d.entity_id).filter(Boolean)));
      
      // Sort by length descending to match longer IDs first (e.g., "Student-12" before "Student")
      uniqueEntityIds.sort((a, b) => (b?.length || 0) - (a?.length || 0));
      
      for (const entityId of uniqueEntityIds) {
        if (!entityId) continue;
        // Check if entity ID appears in query (case insensitive, with word boundaries)
        const escapedId = entityId.replace(/[-_]/g, '[-_\\s]?');
        const entityPattern = new RegExp(`\\b${escapedId}\\b`, 'i');
        if (entityPattern.test(queryText)) {
          entitySearch = entityId.toLowerCase();
          console.log(`[Quick Ask] Found entity "${entityId}" in query via fallback scan`);
          // If this is a road query, also flag for road lookup
          if (isRoadQuery && !mapAction.requestRoads) {
            mapAction.requestRoads = true;
            console.log(`[Quick Ask] Enabling road lookup for entity "${entityId}"`);
          }
          break;
        }
      }
    }
    
    if ((entitySearch || mapAction.requestRoute) && onMapAction && entitySearch) {
      setQuickAskResponse(`Fetching route data for "${entitySearch}"...`);
      
      // Find matching entity
      const normalizedSearch = entitySearch.replace(/[-_\s]/g, '').toLowerCase();
      const normalize = (str: string) => str?.toLowerCase().replace(/[-_\s]/g, '') || '';
      
      const matchingDevice = devices.find(d => 
        normalize(d.entity_id || '') === normalizedSearch ||
        normalize(d.entity_id || '').includes(normalizedSearch) ||
        normalizedSearch.includes(normalize(d.entity_id || ''))
      );
      
      // Try to get entity ID from matching device OR use search term directly
      const entityIdToFetch = matchingDevice?.entity_id || entitySearch;
      
      if (entityIdToFetch) {
        try {
          console.log(`[Quick Ask] Fetching tracks for entity: ${entityIdToFetch} (from device: ${!!matchingDevice})`);
          const trackResponse = await deviceDataService.getDeviceTracks(entityIdToFetch, 7); // Last 7 days
          console.log(`[Quick Ask] Track response:`, trackResponse);
          
          if (trackResponse?.tracks && trackResponse.tracks.length > 0) {
            routeData = [{
              entityId: entityIdToFetch,
              tracks: trackResponse.tracks,
              color: '#2196F3', // Blue route
            }];
            
            // Calculate route bounds for zooming
            const lats = trackResponse.tracks.map((t: TrackPoint) => t.latitude);
            const lons = trackResponse.tracks.map((t: TrackPoint) => t.longitude);
            const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
            const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
            
            onMapAction({
              type: 'show_entity_route',
              entityId: entityIdToFetch,
              routes: routeData,
              latitude: centerLat,
              longitude: centerLon,
              zoom: 12,
            });
            
            // If road lookup was requested, get road names for the route
            if (mapAction.requestRoads) {
              try {
                console.log(`[Quick Ask] Road lookup requested, processing ${trackResponse.tracks.length} track points`);
                
                // Simplify coordinates for API call (take every Nth point to reduce payload)
                const tracks = trackResponse.tracks;
                const maxPoints = 25; // Mapbox limit
                const step = Math.ceil(tracks.length / maxPoints);
                const simplifiedCoords: [number, number][] = [];
                
                for (let i = 0; i < tracks.length; i += step) {
                  simplifiedCoords.push([tracks[i].latitude, tracks[i].longitude]);
                }
                // Always include the last point
                if (simplifiedCoords.length > 0) {
                  const lastTrack = tracks[tracks.length - 1];
                  const lastSimplified = simplifiedCoords[simplifiedCoords.length - 1];
                  if (lastTrack.latitude !== lastSimplified[0] || lastTrack.longitude !== lastSimplified[1]) {
                    simplifiedCoords.push([lastTrack.latitude, lastTrack.longitude]);
                  }
                }
                
                console.log(`[Quick Ask] Sending ${simplifiedCoords.length} simplified coordinates to road lookup`);
                const roadData = await pathfinderApi.getRouteRoads(simplifiedCoords, 'driving');
                
                if (roadData.roads && roadData.roads.length > 0) {
                  const roadList = roadData.roads
                    .filter(r => r.name && r.name !== 'Unknown Road')
                    .map(r => {
                      const distanceKm = (r.distance / 1000).toFixed(1);
                      const distanceMi = (r.distance / 1609.34).toFixed(1);
                      return `• **${r.name}** - ${distanceMi} mi (${distanceKm} km)`;
                    })
                    .join('\n');
                  
                  const totalDistMi = (roadData.total_distance / 1609.34).toFixed(1);
                  const totalDistKm = (roadData.total_distance / 1000).toFixed(1);
                  const totalDurationMin = Math.round(roadData.total_duration / 60);
                  
                  mapActionPrefix = `**Roads Traveled by ${entityIdToFetch}**\n\n` +
                    `**Route Summary:** ${totalDistMi} mi (${totalDistKm} km) | ~${totalDurationMin} min driving\n\n` +
                    `**Roads:**\n${roadList}\n\n` +
                    `*Route displayed on map with ${trackResponse.tracks.length} track points*\n\n---\n\n`;
                } else {
                  mapActionPrefix = `**Route Displayed!** Showing **${trackResponse.tracks.length} track points** for **${entityIdToFetch}**.\n\n` +
                    `*Road names could not be determined - track data may not follow roads or API unavailable.*\n\n---\n\n`;
                }
              } catch (roadError) {
                console.error('[Quick Ask] Road lookup failed:', roadError);
                mapActionPrefix = `**Route Displayed!** Showing **${trackResponse.tracks.length} track points** for **${entityIdToFetch}** over the last 7 days.\n\n` +
                  `*Road lookup failed - showing track points only.*\n\n---\n\n`;
              }
            } else {
              mapActionPrefix = `**Route Displayed!** Showing **${trackResponse.tracks.length} track points** for **${entityIdToFetch}** over the last 7 days.\n\n---\n\n`;
            }
          } else {
            mapActionPrefix = `**No Route Data!** No movement history found for **${entityIdToFetch}** in the last 7 days. The entity may not have GPS track data recorded.\n\n---\n\n`;
          }
        } catch (e) {
          console.error('[Quick Ask] Failed to fetch track data:', e);
          mapActionPrefix = `**Error!** Failed to fetch route data for **${entityIdToFetch}**: ${e}\n\n---\n\n`;
        }
      }
    }

    // Handle dwell display request
    const dwellEntitySearch = mapAction.dwellEntitySearch;
    console.log('[Quick Ask] Dwell display check:', { dwellEntitySearch, requestDwells: mapAction.requestDwells, hasOnMapAction: !!onMapAction });
    
    if ((dwellEntitySearch || mapAction.requestDwells) && onMapAction && dwellEntitySearch) {
      setQuickAskResponse(`Loading dwell locations for "${dwellEntitySearch}"...`);
      
      // Find matching entity
      const normalizedSearch = dwellEntitySearch.replace(/[-_\s]/g, '').toLowerCase();
      const normalize = (str: string) => str?.toLowerCase().replace(/[-_\s]/g, '') || '';
      
      console.log(`[Quick Ask] Searching for dwells matching "${dwellEntitySearch}" (normalized: "${normalizedSearch}"). Total dwells available: ${dwells.length}`);
      
      // Find dwells for this entity from local data
      let entityDwells = dwells.filter(d => {
        const entityId = d.entity_id || '';
        const match = normalize(entityId) === normalizedSearch ||
               normalize(entityId).includes(normalizedSearch) ||
               normalizedSearch.includes(normalize(entityId));
        if (match) console.log(`[Quick Ask] Matched dwell for entity: ${entityId}`);
        return match;
      });
      
      console.log(`[Quick Ask] Found ${entityDwells.length} dwells in local data for "${dwellEntitySearch}"`);
      
      // If no dwells found locally, try fetching from API directly
      if (entityDwells.length === 0) {
        console.log(`[Quick Ask] No local dwells found, fetching from API for "${dwellEntitySearch}"`);
        try {
          const apiResponse = await copDwellsService.getDwells(
            undefined, // courseId
            undefined, // deviceType
            undefined, // entityType
            14, // days - look back 2 weeks for historic data
            200, // limit
            dwellEntitySearch // entityId filter
          );
          if (apiResponse?.dwells && apiResponse.dwells.length > 0) {
            entityDwells = apiResponse.dwells;
            console.log(`[Quick Ask] Fetched ${entityDwells.length} dwells from API for "${dwellEntitySearch}"`);
          }
        } catch (apiError) {
          console.error('[Quick Ask] Failed to fetch dwells from API:', apiError);
        }
      }
      
      if (entityDwells.length > 0) {
        // Filter dwells with valid coordinates
        const validDwells = entityDwells.filter(d => d.latitude && d.longitude);
        
        // Cluster nearby dwells (within ~150m of each other)
        const CLUSTER_THRESHOLD_DEGREES = 0.0015; // ~150m at equator
        const clusters: Array<{
          dwells: typeof validDwells;
          centerLat: number;
          centerLon: number;
          totalDuration: number;
          visitCount: number;
        }> = [];
        
        const assignedToClusters = new Set<number>();
        
        for (let i = 0; i < validDwells.length; i++) {
          if (assignedToClusters.has(i)) continue;
          
          const dwell = validDwells[i];
          const clusterDwells = [dwell];
          assignedToClusters.add(i);
          
          // Find all nearby dwells
          for (let j = i + 1; j < validDwells.length; j++) {
            if (assignedToClusters.has(j)) continue;
            
            const other = validDwells[j];
            const latDiff = Math.abs(dwell.latitude! - other.latitude!);
            const lonDiff = Math.abs(dwell.longitude! - other.longitude!);
            
            if (latDiff < CLUSTER_THRESHOLD_DEGREES && lonDiff < CLUSTER_THRESHOLD_DEGREES) {
              clusterDwells.push(other);
              assignedToClusters.add(j);
            }
          }
          
          // Calculate cluster center (average of all points)
          const clusterLat = clusterDwells.reduce((sum, d) => sum + d.latitude!, 0) / clusterDwells.length;
          const clusterLon = clusterDwells.reduce((sum, d) => sum + d.longitude!, 0) / clusterDwells.length;
          const totalDuration = clusterDwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0);
          
          clusters.push({
            dwells: clusterDwells,
            centerLat: clusterLat,
            centerLon: clusterLon,
            totalDuration,
            visitCount: clusterDwells.length,
          });
        }
        
        console.log(`[Quick Ask] Clustered ${validDwells.length} dwells into ${clusters.length} clusters`);
        
        // Create dwell circles from clusters
        const dwellCircles: DwellCircle[] = clusters.map((cluster, idx) => ({
          id: `dwell-cluster-${entityDwells[0].entity_id}-${idx}`,
          latitude: cluster.centerLat,
          longitude: cluster.centerLon,
          // Scale radius by duration: min 75m, max 300m, plus bonus for multiple visits
          radiusMeters: Math.min(300, Math.max(75, (cluster.totalDuration / 60 * 3) + (cluster.visitCount * 15))),
          entityId: entityDwells[0].entity_id || 'Unknown',
          deviceId: cluster.dwells[0].device_id,
          durationSeconds: cluster.totalDuration,
          visitCount: cluster.visitCount,
          startTime: cluster.dwells[0].start_time,
          endTime: cluster.dwells[cluster.dwells.length - 1].end_time,
        }));
        
        console.log(`[Quick Ask] Created ${dwellCircles.length} clustered dwell circles:`, dwellCircles.slice(0, 3));
        
        // Calculate center for zooming
        const lats = dwellCircles.map(d => d.latitude);
        const lons = dwellCircles.map(d => d.longitude);
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
        
        // Calculate total dwell time
        const totalSeconds = dwellCircles.reduce((sum, d) => sum + d.durationSeconds, 0);
        const totalHours = Math.floor(totalSeconds / 3600);
        const totalMins = Math.floor((totalSeconds % 3600) / 60);
        const timeStr = totalHours > 0 ? `${totalHours}h ${totalMins}m` : `${totalMins}m`;
        
        console.log(`[Quick Ask] Triggering show_entity_dwells action with ${dwellCircles.length} clustered circles at center (${centerLat}, ${centerLon})`);
        
        onMapAction({
          type: 'show_entity_dwells',
          entityId: entityDwells[0].entity_id,
          dwellCircles,
          latitude: centerLat,
          longitude: centerLon,
          zoom: 12,
        });
        
        mapActionPrefix = `**Dwell Locations Displayed!** Showing **${dwellCircles.length} clustered locations** (from ${validDwells.length} dwells) for **${entityDwells[0].entity_id}**.\n- Total dwell time: **${timeStr}**\n- Circle size indicates duration + visit frequency\n\n---\n\n`;
      } else {
        mapActionPrefix = `**No Dwell Data!** No dwell/stop locations found for entity matching "${dwellEntitySearch}".\n\n---\n\n`;
      }
    }
    
    // Check if user is asking about nearby places, surroundings, or locations
    const nearbyKeywords = /what('?s| is) (near|nearby|around|at|close)|nearby|surroundings|neighborhood|around (him|her|them|it)|locations? around|what.*(location|place|business|store|shop)/i;
    const wantsLocationContext = nearbyKeywords.test(queryText);
    console.log('[Quick Ask] Wants location context:', wantsLocationContext, 'Query:', queryText);
    
    if (wantsLocationContext && !locationContext && mapViewport) {
      try {
        locationContext = await areaIntelService.getLocationContext(
          mapViewport.latitude,
          mapViewport.longitude
        );
        console.log('[Quick Ask] Location context for map center:', locationContext);
      } catch (e) {
        console.log('[Quick Ask] Could not fetch location context:', e);
      }
    }

    // === AREA ANALYSIS ===
    // If an analysis area is drawn, filter data to only include items within that area
    const isAreaAnalysis = !!analysisArea;
    const areaDevices = isAreaAnalysis ? filterDevicesInArea(analysisArea) : devices;
    const areaDwells = isAreaAnalysis ? filterDwellsInArea(analysisArea) : dwells;
    const areaBounds = isAreaAnalysis ? getAreaBounds(analysisArea) : null;
    
    // Add area context to the query if area is selected
    let areaContextPrefix = '';
    if (isAreaAnalysis) {
      const uniqueEntitiesInArea = new Set([
        ...areaDevices.map(d => d.entity_id).filter(Boolean),
        ...areaDwells.map(d => d.entity_id).filter(Boolean),
      ]);
      
      areaContextPrefix = `**Analyzing Selected Area**\n`;
      areaContextPrefix += `Found: ${areaDevices.length} devices, ${areaDwells.length} dwells, ${uniqueEntitiesInArea.size} unique entities\n\n---\n\n`;
      
      console.log(`[Quick Ask] Area analysis mode - Devices: ${areaDevices.length}, Dwells: ${areaDwells.length}`);
    }
    
    // Build context from current state (use area-filtered data if applicable)
    const contextDevices = isAreaAnalysis ? areaDevices : devices;
    const contextDwells = isAreaAnalysis ? areaDwells : dwells;
    
    const entityCounts = contextDevices.reduce((acc, d) => {
      const type = d.entity_type || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
const staleDevices = contextDevices.filter(d => (d.last_seen_minutes_ago ?? 0) > 60).length;
    const recentlyActive = contextDevices.filter(d => (d.last_seen_minutes_ago ?? 999) <= 5).length;

    // Calculate dwell metrics (using area-filtered dwells if applicable)
    const dwellMetrics = {
      totalDwells: contextDwells.length,
      uniqueEntitiesWithDwells: new Set(contextDwells.map(d => d.entity_id)).size,
      totalDwellTimeSeconds: contextDwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0),
      averageDwellSeconds: contextDwells.length > 0 
        ? contextDwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0) / contextDwells.length 
        : 0,
      longDwells: contextDwells.filter(d => (d.duration_seconds || 0) > 14400).length, // > 4 hours
      recentDwells: contextDwells.filter(d => {
        const startTime = new Date(d.start_time);
        const now = new Date();
        return (now.getTime() - startTime.getTime()) < 24 * 60 * 60 * 1000; // Last 24h
      }).length,
    };

    // === CO-LOCATION DETECTION ===
    // Find instances where different entities' devices were at the same location at the same time
    // This detects potential meetings, encounters, or co-travel patterns
    interface CoLocationEvent {
      location: { latitude: number; longitude: number };
      entities: Array<{
        entityId: string;
        entityType: string;
        deviceId: string;
        deviceType: string;
        dwellStart: string;
        dwellEnd: string;
        duration: string;
        durationSeconds: number;
      }>;
      overlapStart: string;
      overlapEnd: string;
      overlapMinutes: number;
      locationKey: string;
    }

    const detectCoLocations = (): CoLocationEvent[] => {
      const coLocations: CoLocationEvent[] = [];
      
      console.log(`[Co-Location] Starting detection with ${dwells.length} dwells`);
      if (dwells.length < 2) {
        console.log('[Co-Location] Not enough dwells (<2) for co-location detection');
        return coLocations;
      }
      
      // Count unique entities in dwells
      const uniqueEntities = new Set(dwells.map(d => d.entity_id).filter(Boolean));
      console.log(`[Co-Location] Unique entities in dwells: ${uniqueEntities.size}`, Array.from(uniqueEntities).slice(0, 5));
      
      // Helper to check if two time ranges overlap
      const getTimeOverlap = (start1: Date, end1: Date, start2: Date, end2: Date): { start: Date; end: Date } | null => {
        const overlapStart = new Date(Math.max(start1.getTime(), start2.getTime()));
        const overlapEnd = new Date(Math.min(end1.getTime(), end2.getTime()));
        if (overlapStart < overlapEnd) {
          return { start: overlapStart, end: overlapEnd };
        }
        return null;
      };
      
      // Helper: Calculate distance between two points in feet
      const distanceFeet = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const R = 20902231; // Earth's radius in feet
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
      };
      
      // Group dwells by approximate location (within ~500 feet / 150m)
      const locationGroups = new Map<string, typeof dwells>();
      
      for (const dwell of dwells) {
        if (!dwell.latitude || !dwell.longitude) continue;
        
        // Round to ~150m precision for grouping
        const locKey = `${dwell.latitude.toFixed(3)},${dwell.longitude.toFixed(3)}`;
        
        // Check if this location is close to an existing group
        let foundGroup = false;
        const entries = Array.from(locationGroups.entries());
        for (const [key, group] of entries) {
          const [groupLat, groupLon] = key.split(',').map(Number);
          const dist = distanceFeet(dwell.latitude, dwell.longitude, groupLat, groupLon);
          if (dist < 500) { // Within 500 feet
            group.push(dwell);
            foundGroup = true;
            break;
          }
        }
        
        if (!foundGroup) {
          locationGroups.set(locKey, [dwell]);
        }
      }
      
      console.log(`[Co-Location] Created ${locationGroups.size} location groups`);
      
      // For each location group, check for time overlaps between different entities
      let groupsWithMultipleEntities = 0;
      for (const [locKey, locationDwells] of Array.from(locationGroups.entries())) {
        if (locationDwells.length < 2) continue;
        
        // Get unique entities at this location
        const entityDwells = new Map<string, typeof locationDwells>();
        for (const dwell of locationDwells) {
          const key = dwell.entity_id;
          if (!entityDwells.has(key)) {
            entityDwells.set(key, []);
          }
          entityDwells.get(key)!.push(dwell);
        }
        
        // Need at least 2 different entities
        if (entityDwells.size < 2) continue;
        groupsWithMultipleEntities++;
        
        // Check each pair of entities for time overlap
        const entityIds = Array.from(entityDwells.keys());
        for (let i = 0; i < entityIds.length; i++) {
          for (let j = i + 1; j < entityIds.length; j++) {
            const entity1Dwells = entityDwells.get(entityIds[i])!;
            const entity2Dwells = entityDwells.get(entityIds[j])!;
            
            // Check each dwell pair for time overlap
            for (const dwell1 of entity1Dwells) {
              for (const dwell2 of entity2Dwells) {
                const start1 = new Date(dwell1.start_time);
                const end1 = new Date(dwell1.end_time);
                const start2 = new Date(dwell2.start_time);
                const end2 = new Date(dwell2.end_time);
                
                const overlap = getTimeOverlap(start1, end1, start2, end2);
                if (overlap) {
                  const overlapMinutes = Math.round((overlap.end.getTime() - overlap.start.getTime()) / 60000);
                  
                  // Only report overlaps of at least 5 minutes
                  if (overlapMinutes >= 5) {
                    // Check if we already have a co-location at this location
                    const existing = coLocations.find(c => c.locationKey === locKey);
                    
                    if (existing) {
                      // Add entities to existing co-location if not already there
                      const addEntity = (dwell: typeof dwell1) => {
                        if (!existing.entities.find(e => e.deviceId === dwell.device_id)) {
                          existing.entities.push({
                            entityId: dwell.entity_id,
                            entityType: devices.find(d => d.entity_id === dwell.entity_id)?.entity_type || 'Unknown',
                            deviceId: dwell.device_id,
                            deviceType: dwell.device_type,
                            dwellStart: dwell.start_time,
                            dwellEnd: dwell.end_time,
                            duration: dwell.duration,
                            durationSeconds: dwell.duration_seconds,
                          });
                        }
                      };
                      addEntity(dwell1);
                      addEntity(dwell2);
                      // Update overlap if this one is longer
                      if (overlapMinutes > existing.overlapMinutes) {
                        existing.overlapStart = overlap.start.toISOString();
                        existing.overlapEnd = overlap.end.toISOString();
                        existing.overlapMinutes = overlapMinutes;
                      }
                    } else {
                      coLocations.push({
                        location: { latitude: dwell1.latitude, longitude: dwell1.longitude },
                        entities: [
                          {
                            entityId: dwell1.entity_id,
                            entityType: devices.find(d => d.entity_id === dwell1.entity_id)?.entity_type || 'Unknown',
                            deviceId: dwell1.device_id,
                            deviceType: dwell1.device_type,
                            dwellStart: dwell1.start_time,
                            dwellEnd: dwell1.end_time,
                            duration: dwell1.duration,
                            durationSeconds: dwell1.duration_seconds,
                          },
                          {
                            entityId: dwell2.entity_id,
                            entityType: devices.find(d => d.entity_id === dwell2.entity_id)?.entity_type || 'Unknown',
                            deviceId: dwell2.device_id,
                            deviceType: dwell2.device_type,
                            dwellStart: dwell2.start_time,
                            dwellEnd: dwell2.end_time,
                            duration: dwell2.duration,
                            durationSeconds: dwell2.duration_seconds,
                          },
                        ],
                        overlapStart: overlap.start.toISOString(),
                        overlapEnd: overlap.end.toISOString(),
                        overlapMinutes,
                        locationKey: locKey,
                      });
                    }
                  }
                }
              }
            }
          }
        }
      }
      
      console.log(`[Co-Location] Groups with multiple entities: ${groupsWithMultipleEntities}`);
      console.log(`[Co-Location] Found ${coLocations.length} co-location events`);
      
      // Sort by overlap duration (longest first)
      return coLocations.sort((a, b) => b.overlapMinutes - a.overlapMinutes);
    };

    const coLocationEvents = detectCoLocations();

    // Check if user wants to show co-locations on map (runs after co-location detection)
    const coLocationMarkerPattern = /(?:show|mark|display|plot)\s+(?:the\s+)?(?:co-?locations?|meetings?|encounters?)\s*(?:on\s+)?(?:the\s+)?(?:map)?/i;
    if (coLocationMarkerPattern.test(queryText) && coLocationEvents.length > 0) {
      const markerResult = addCoLocationMarkers(coLocationEvents);
      if (markerResult.executed && markerResult.message) {
        mapActionPrefix = markerResult.message + '\n\n---\n\n';
      } else if (markerResult.message) {
        mapActionPrefix = `${markerResult.message}\n\n---\n\n`;
      }
    }

    // === CO-TRAVELER ANALYSIS ===
    // Detect if user is asking about co-travelers for a specific entity
    let coTravelerData: any = null;
    const coTravelerPattern = /(?:co-?traveler|travel.*with|traveling.*together|who.*travel|co-?location.*analysis)/i;
    const isCoTravelerQuery = coTravelerPattern.test(queryText);
    
    if (isCoTravelerQuery) {
      // Try to find an entity mentioned in the query
      const entityPattern = /(?:for|of)\s+["']?([a-zA-Z]+[-_]?[a-zA-Z0-9_-]+)["']?/i;
      const entityMatch = queryText.match(entityPattern);
      let targetEntity = entityMatch?.[1];
      
      // If no entity in pattern, check if there's a device with matching ID
      if (!targetEntity) {
        for (const device of devices) {
          if (device.entity_id && queryText.toLowerCase().includes(device.entity_id.toLowerCase())) {
            targetEntity = device.entity_id;
            break;
          }
        }
      }
      
      if (targetEntity) {
        try {
          console.log(`[Quick Ask] Fetching co-travelers for entity: ${targetEntity}`);
          const coTravelerResponse = await deviceDataService.getEntityCoTravelers(targetEntity, 7, 100, 3);
          console.log(`[Quick Ask] Co-traveler response:`, coTravelerResponse);
          
          if (coTravelerResponse?.co_travelers?.length > 0) {
            coTravelerData = {
              primary_entity: targetEntity,
              co_travelers: coTravelerResponse.co_travelers,
              days_analyzed: coTravelerResponse.days_analyzed,
              total_found: coTravelerResponse.count
            };
            mapActionPrefix = `**Co-Traveler Analysis Complete!** Found **${coTravelerResponse.count} entities** that traveled with **${targetEntity}** in the last ${coTravelerResponse.days_analyzed} days.\n\n---\n\n`;
          } else {
            coTravelerData = {
              primary_entity: targetEntity,
              co_travelers: [],
              days_analyzed: coTravelerResponse?.days_analyzed || 7,
              total_found: 0,
              message: `No co-travelers detected for ${targetEntity} in the analyzed time period.`
            };
          }
        } catch (e) {
          console.error('[Quick Ask] Co-traveler analysis failed:', e);
          coTravelerData = {
            error: `Co-traveler analysis failed: ${e}`,
            primary_entity: targetEntity
          };
        }
      } else {
        // No specific entity mentioned - scan active entities for any co-travelers
        console.log(`[Quick Ask] No entity specified for co-traveler query, scanning active entities...`);
        
        // Get entities with recent activity (sorted by last seen)
        const activeEntities = devices
          .filter(d => d.entity_id && (d.last_seen_minutes_ago ?? 999) < 1440) // Active in last 24 hours
          .sort((a, b) => (a.last_seen_minutes_ago ?? 999) - (b.last_seen_minutes_ago ?? 999))
          .map(d => d.entity_id!)
          .filter((v, i, a) => a.indexOf(v) === i) // Unique
          .slice(0, 5); // Check top 5 most active
        
        if (activeEntities.length > 0) {
          console.log(`[Quick Ask] Scanning ${activeEntities.length} active entities for co-travelers:`, activeEntities);
          
          const allCoTravelers: any[] = [];
          
          // Check each active entity for co-travelers (in parallel)
          const promises = activeEntities.map(async (entityId) => {
            try {
              const response = await deviceDataService.getEntityCoTravelers(entityId, 7, 100, 3);
              if (response?.co_travelers?.length > 0) {
                return {
                  primary_entity: entityId,
                  co_travelers: response.co_travelers,
                  count: response.count
                };
              }
              return null;
            } catch (e) {
              console.log(`[Quick Ask] Co-traveler check failed for ${entityId}:`, e);
              return null;
            }
          });
          
          const results = await Promise.all(promises);
          
          results.forEach((result) => {
            if (result) {
              allCoTravelers.push(result);
            }
          });
          
          if (allCoTravelers.length > 0) {
            // Found co-travelers!
            const totalPairs = allCoTravelers.reduce((sum, r) => sum + r.count, 0);
            coTravelerData = {
              scan_results: allCoTravelers,
              entities_scanned: activeEntities.length,
              total_pairs_found: totalPairs,
              message: `Scanned ${activeEntities.length} active entities and found ${totalPairs} co-traveler relationship(s).`
            };
            mapActionPrefix = `**Co-Traveler Scan Complete!** Found **${totalPairs} co-traveler relationship(s)** across ${activeEntities.length} scanned entities.\n\n---\n\n`;
          } else {
            coTravelerData = {
              entities_scanned: activeEntities.length,
              total_pairs_found: 0,
              message: `Scanned ${activeEntities.length} active entities but no co-traveler patterns were detected in the last 7 days.`,
              suggestion: `Active entities checked: ${activeEntities.join(', ')}. You can ask about a specific entity for deeper analysis.`
            };
          }
        } else {
          coTravelerData = {
            message: "No active entities found to scan for co-travelers. Ensure devices have recent GPS track data."
          };
        }
      }
    }

    // Calculate device status breakdown
    const deviceStatusBreakdown = {
      online: devices.filter(d => (d.last_seen_minutes_ago ?? 999) <= 5).length,
      recent: devices.filter(d => (d.last_seen_minutes_ago ?? 999) > 5 && (d.last_seen_minutes_ago ?? 999) <= 30).length,
      idle: devices.filter(d => (d.last_seen_minutes_ago ?? 999) > 30 && (d.last_seen_minutes_ago ?? 999) <= 60).length,
      offline: devices.filter(d => (d.last_seen_minutes_ago ?? 999) > 60).length,
      moving: devices.filter(d => (d.speed ?? 0) > 0.5).length,
      stationary: devices.filter(d => (d.speed ?? 0) <= 0.5 && d.latitude && d.longitude).length,
    };

    // Calculate geofence metrics with location info for proximity checking
    // Helper to get center point from polygon geometry
    const getGeofenceCenter = (geometry: GeofenceData['geometry']) => {
      if (!geometry?.coordinates?.[0]?.length) return null;
      const coords = geometry.coordinates[0];
      const lats = coords.map(c => c[1]);
      const lons = coords.map(c => c[0]);
      return {
        latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
        longitude: (Math.min(...lons) + Math.max(...lons)) / 2,
      };
    };
    
    const geofenceMetrics = {
      totalGeofences: geofences.length,
      activeGeofences: geofences.filter(g => g.is_active !== false).length,
      geofenceTypes: geofences.reduce((acc, g) => {
        const type = g.type || 'standard';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      // Include location data so backend can determine proximity
      geofencesWithLocation: geofences.slice(0, 20).map(g => {
        const center = getGeofenceCenter(g.geometry);
        return {
          name: g.name,
          description: g.description || undefined,
          latitude: center?.latitude,
          longitude: center?.longitude,
          type: g.type || 'standard',
        };
      }).filter(g => g.latitude && g.longitude),
    };

// === ENHANCED ENTITY DETECTION ===
    // Improvements:
    // 1. Better entity ID matching with word boundaries (Student-12 won't match Student-123)
    // 2. Device type filtering (watch/phone/vehicle)
    // 3. Multiple entity comparison with distance calculation
    // 4. Device type synonyms (cell/mobile -> phone)
    
    let entitySpecificData: any = null;
    let comparisonData: any = null;
    const queryLower = queryText.toLowerCase();

    // Device type detection with synonyms
    const deviceTypeSynonyms: Record<string, string> = {
      'phone': 'phone', 'cell': 'phone', 'mobile': 'phone', 'cellular': 'phone',
      'watch': 'watch', 'smartwatch': 'watch', 'wearable': 'watch',
      'vehicle': 'vehicle', 'car': 'vehicle', 'truck': 'vehicle', 'van': 'vehicle',
    };
    
    const detectDeviceType = (query: string): string | null => {
      for (const [synonym, type] of Object.entries(deviceTypeSynonyms)) {
        // Use word boundary matching
        const regex = new RegExp(`\\b${synonym}\\b`, 'i');
        if (regex.test(query)) {
          return type;
        }
      }
      return null;
    };
    
    const requestedDeviceType = detectDeviceType(queryLower);
    console.log('[Quick Ask] Requested device type:', requestedDeviceType);

    // Helper: Calculate distance between two coordinates in miles
    const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
      const R = 3959; // Earth's radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    };

    // Helper: Find entity with improved matching (word boundaries)
    const findEntityMatch = (query: string, excludeIds: string[] = []): { device: DeviceData | null; dwell: DwellData | null } => {
      // Collect all unique entity IDs from devices and dwells
      const allEntityIds = new Set<string>();
      devices.forEach(d => { if (d.entity_id) allEntityIds.add(d.entity_id); });
      dwells.forEach(d => { if (d.entity_id) allEntityIds.add(d.entity_id); });
      
      // Sort by length descending to match longest first (Student-123 before Student-12)
      const sortedIds = Array.from(allEntityIds)
        .filter(id => !excludeIds.includes(id))
        .sort((a, b) => b.length - a.length);
      
      for (const entityId of sortedIds) {
        // Create a regex with word boundaries for exact matching
        // Escape special regex characters in entity ID
        const escapedId = entityId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\b${escapedId}\\b`, 'i');
        
        if (regex.test(query)) {
          // Found a match - get device and/or dwell data
          const device = devices.find(d => d.entity_id === entityId) || null;
          const dwell = dwells.find(d => d.entity_id === entityId) || null;
          return { device, dwell };
        }
      }
      
      return { device: null, dwell: null };
    };

    // Helper: Build entity data object
    const buildEntityData = (
      entityId: string, 
      mentionedEntity: DeviceData | null, 
      entityFromDwell: DwellData | null,
      filterDeviceType: string | null = null
    ) => {
      const entityType = mentionedEntity?.entity_type || 'Unknown';
      
      // Get ALL dwells for this entity
      const entityDwells = dwells.filter(d => d.entity_id === entityId);
      const totalDwellTime = entityDwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0);
      
      // Group dwells by device_id
      const deviceMap = new Map<string, { 
        deviceId: string; 
        deviceType: string; 
        dwellCount: number; 
        totalDwellSeconds: number;
        dwells: typeof entityDwells;
      }>();
      
      entityDwells.forEach(d => {
        const existing = deviceMap.get(d.device_id);
        if (existing) {
          existing.dwellCount++;
          existing.totalDwellSeconds += d.duration_seconds || 0;
          existing.dwells.push(d);
        } else {
          deviceMap.set(d.device_id, {
            deviceId: d.device_id,
            deviceType: d.device_type || 'Unknown',
            dwellCount: 1,
            totalDwellSeconds: d.duration_seconds || 0,
            dwells: [d],
          });
        }
      });
      
// Get all devices for this entity from the devices array
      const entityDevices = devices.filter(d => d.entity_id === entityId);

      // Build device summaries
      const deviceSummaries: Array<{
        deviceId: string;
        deviceType: string;
        dwellCount: number;
        totalDwellSeconds: number;
        isOnline: boolean;
        lastSeenMinutesAgo?: number;
        latitude?: number;
        longitude?: number;
        speed?: number;
        battery?: number;
        recentDwells: any[];
      }> = [];

      // First add devices from dwells
      Array.from(deviceMap.values()).forEach(dev => {
        const liveDevice = entityDevices.find(ed => ed.device_id === dev.deviceId);
        deviceSummaries.push({
          deviceId: dev.deviceId,
          deviceType: dev.deviceType,
          dwellCount: dev.dwellCount,
          totalDwellSeconds: dev.totalDwellSeconds,
          isOnline: liveDevice ? (liveDevice.last_seen_minutes_ago ?? 999) <= 30 : false,
          lastSeenMinutesAgo: liveDevice?.last_seen_minutes_ago,
          latitude: liveDevice?.latitude,
          longitude: liveDevice?.longitude,
          speed: liveDevice?.speed,
          battery: liveDevice?.battery_level,
          recentDwells: dev.dwells.slice(0, 3).map(dw => ({
            duration: dw.duration,
            durationSeconds: dw.duration_seconds,
            startTime: dw.start_time,
            latitude: dw.latitude,
            longitude: dw.longitude,
          })),
        });
      });

      // Then add any live devices NOT in the dwell map
      entityDevices.forEach(dev => {
        if (!deviceMap.has(dev.device_id)) {
          deviceSummaries.push({
            deviceId: dev.device_id,
            deviceType: dev.device_type || 'Unknown',
            dwellCount: 0,
            totalDwellSeconds: 0,
            isOnline: (dev.last_seen_minutes_ago ?? 999) <= 30,
            lastSeenMinutesAgo: dev.last_seen_minutes_ago,
            latitude: dev.latitude,
            longitude: dev.longitude,
            speed: dev.speed,
            battery: dev.battery_level,
            recentDwells: [],
          });
        }
      });

      // Filter by device type if requested
      let filteredDeviceSummaries = deviceSummaries;
      let focusedDevice: typeof deviceSummaries[0] | null = null;
      
      if (filterDeviceType) {
        filteredDeviceSummaries = deviceSummaries.filter(d => 
          d.deviceType.toLowerCase() === filterDeviceType.toLowerCase()
        );
        focusedDevice = filteredDeviceSummaries[0] || null;
        console.log(`[Quick Ask] Filtered to ${filterDeviceType}: found ${filteredDeviceSummaries.length} devices`);
      }

      // Determine primary location - use focused device if available
      let primaryLat = mentionedEntity?.latitude || entityFromDwell?.latitude;
      let primaryLon = mentionedEntity?.longitude || entityFromDwell?.longitude;
      let primaryLastSeen = mentionedEntity?.last_seen_minutes_ago;
      
      if (focusedDevice) {
        primaryLat = focusedDevice.latitude || primaryLat;
        primaryLon = focusedDevice.longitude || primaryLon;
        primaryLastSeen = focusedDevice.lastSeenMinutesAgo;
      }

      return {
        entityId,
        entityType,
        requestedDeviceType: filterDeviceType,
        focusedDevice: focusedDevice ? {
          deviceId: focusedDevice.deviceId,
          deviceType: focusedDevice.deviceType,
          isOnline: focusedDevice.isOnline,
          lastSeenMinutesAgo: focusedDevice.lastSeenMinutesAgo,
          latitude: focusedDevice.latitude,
          longitude: focusedDevice.longitude,
          speed: focusedDevice.speed,
          battery: focusedDevice.battery,
          dwellCount: focusedDevice.dwellCount,
          totalDwellSeconds: focusedDevice.totalDwellSeconds,
        } : null,
        totalDevicesForEntity: Math.max(entityDevices.length, deviceMap.size),
        devices: deviceSummaries,
        filteredDevices: filterDeviceType ? filteredDeviceSummaries : null,
        lastSeenMinutesAgo: primaryLastSeen,
        isOnline: mentionedEntity ? (mentionedEntity.last_seen_minutes_ago ?? 999) <= 30 : false,
        hasLocation: !!(primaryLat && primaryLon),
        latitude: primaryLat,
        longitude: primaryLon,
        speed: mentionedEntity?.speed,
        dwellCount: entityDwells.length,
        totalDwellTimeSeconds: totalDwellTime,
        foundInDwellsOnly: !mentionedEntity && !!entityFromDwell,
        recentDwells: entityDwells.slice(0, 5).map(d => ({
          duration: d.duration,
          durationSeconds: d.duration_seconds,
          startTime: d.start_time,
          latitude: d.latitude,
          longitude: d.longitude,
          deviceType: d.device_type,
          deviceId: d.device_id,
        })),
      };
    };

    // Find first entity mentioned
    const firstMatch = findEntityMatch(queryLower);
    let mentionedEntity = firstMatch.device;
    let entityFromDwell = firstMatch.dwell;

    if (mentionedEntity || entityFromDwell) {
      const entityId = mentionedEntity?.entity_id || mentionedEntity?.device_id || 
                       entityFromDwell?.entity_id || 'Unknown';
      
      entitySpecificData = buildEntityData(entityId, mentionedEntity, entityFromDwell, requestedDeviceType);
      
      // Check for second entity (for comparison queries)
      const secondMatch = findEntityMatch(queryLower, [entityId]);
      if (secondMatch.device || secondMatch.dwell) {
        const secondEntityId = secondMatch.device?.entity_id || secondMatch.device?.device_id ||
                               secondMatch.dwell?.entity_id || 'Unknown';
        
        const secondEntityData = buildEntityData(secondEntityId, secondMatch.device, secondMatch.dwell, requestedDeviceType);
        
        // Calculate distance if both have locations
        let distanceMiles: number | null = null;
        if (entitySpecificData.latitude && entitySpecificData.longitude && 
            secondEntityData.latitude && secondEntityData.longitude) {
          distanceMiles = haversineDistance(
            entitySpecificData.latitude, entitySpecificData.longitude,
            secondEntityData.latitude, secondEntityData.longitude
          );
        }
        
        comparisonData = {
          entity1: entitySpecificData,
          entity2: secondEntityData,
          distanceMiles,
          distanceFormatted: distanceMiles !== null 
            ? (distanceMiles < 0.1 ? `${Math.round(distanceMiles * 5280)} feet` 
               : distanceMiles < 1 ? `${(distanceMiles * 5280).toFixed(0)} feet`
               : `${distanceMiles.toFixed(1)} miles`)
            : null,
          bothOnline: entitySpecificData.isOnline && secondEntityData.isOnline,
          bothHaveLocation: entitySpecificData.hasLocation && secondEntityData.hasLocation,
        };
        
        console.log(`[Quick Ask] Comparison: ${entityId} vs ${secondEntityId}, distance: ${comparisonData.distanceFormatted}`);
      }
    }

// If entity was found and user wants location context, fetch it for entity's location
    if (entitySpecificData && entitySpecificData.latitude && entitySpecificData.longitude && !locationContext) {
      try {
        console.log(`[Quick Ask] Fetching location context for entity ${entitySpecificData.entityId} at (${entitySpecificData.latitude}, ${entitySpecificData.longitude})`);
        locationContext = await areaIntelService.getLocationContext(
          entitySpecificData.latitude,
          entitySpecificData.longitude
        );
        console.log('[Quick Ask] Entity location context:', locationContext);
      } catch (e) {
        console.log('[Quick Ask] Could not fetch entity location context:', e);
      }
    }

    // Enrich top dwell locations with place context (address, type, nearby POIs)
    // This helps identify if dwells are at commercial, residential, or named locations
    if (entitySpecificData && entitySpecificData.recentDwells && entitySpecificData.recentDwells.length > 0) {
      try {
        console.log(`[Quick Ask] Enriching ${entitySpecificData.recentDwells.length} dwell locations with place context`);
        
        // Get unique dwell locations (round to ~100m to dedupe nearby locations)
        const uniqueLocations = new Map<string, { lat: number; lon: number; dwells: any[] }>();
        
        for (const dwell of entitySpecificData.recentDwells) {
          if (dwell.latitude && dwell.longitude) {
            // Round to 3 decimal places (~100m precision) for deduplication
            const key = `${dwell.latitude.toFixed(3)},${dwell.longitude.toFixed(3)}`;
            if (!uniqueLocations.has(key)) {
              uniqueLocations.set(key, { lat: dwell.latitude, lon: dwell.longitude, dwells: [dwell] });
            } else {
              uniqueLocations.get(key)!.dwells.push(dwell);
            }
          }
        }
        
        // Sort by total dwell time at each location and take top 5
        const sortedLocations = Array.from(uniqueLocations.values())
          .map(loc => ({
            ...loc,
            totalDwellSeconds: loc.dwells.reduce((sum, d) => sum + (d.durationSeconds || 0), 0),
            visitCount: loc.dwells.length,
          }))
          .sort((a, b) => b.totalDwellSeconds - a.totalDwellSeconds)
          .slice(0, 5);
        
        console.log(`[Quick Ask] Found ${sortedLocations.length} unique dwell locations to enrich`);
        
        // Fetch location context for each in parallel
        const enrichedLocations = await Promise.all(
          sortedLocations.map(async (loc) => {
            try {
              const context = await areaIntelService.getLocationContext(loc.lat, loc.lon);
              
              // Determine location type based on nearby places
              let locationType = 'unknown';
              let placeName: string | null = null;
              
              if (context.nearbyPlaces && context.nearbyPlaces.length > 0) {
                const nearest = context.nearbyPlaces[0];
                placeName = nearest.name;
                
                // Categorize based on nearby POI categories
                const category = nearest.category?.toLowerCase() || '';
                if (category.includes('restaurant') || category.includes('cafe') || category.includes('bar') || 
                    category.includes('food') || category.includes('coffee')) {
                  locationType = 'food_beverage';
                } else if (category.includes('hotel') || category.includes('lodging') || category.includes('motel')) {
                  locationType = 'lodging';
                } else if (category.includes('store') || category.includes('shop') || category.includes('mall') || 
                           category.includes('retail') || category.includes('grocery')) {
                  locationType = 'retail';
                } else if (category.includes('gas') || category.includes('fuel') || category.includes('station')) {
                  locationType = 'gas_station';
                } else if (category.includes('hospital') || category.includes('medical') || category.includes('clinic')) {
                  locationType = 'medical';
                } else if (category.includes('office') || category.includes('business')) {
                  locationType = 'commercial';
                } else if (category.includes('park') || category.includes('recreation')) {
                  locationType = 'recreation';
                } else {
                  locationType = 'commercial'; // Default to commercial if POI found
                }
              }
              
              // Check if it looks residential (has address but no nearby commercial POIs)
              if (locationType === 'unknown' && context.address && !context.nearbyPlaces?.length) {
                locationType = 'residential';
              }
              
              return {
                latitude: loc.lat,
                longitude: loc.lon,
                totalDwellSeconds: loc.totalDwellSeconds,
                visitCount: loc.visitCount,
                locationType,
                placeName,
                address: context.address,
                neighborhood: context.neighborhood,
                city: context.city,
                state: context.state,
                nearbyPlaces: context.nearbyPlaces?.slice(0, 3).map(p => ({
                  name: p.name,
                  category: p.category,
                  distance: p.distance ? `${Math.round(p.distance)}m` : undefined,
                })) || [],
              };
            } catch (e) {
              console.log(`[Quick Ask] Could not enrich location (${loc.lat}, ${loc.lon}):`, e);
              return {
                latitude: loc.lat,
                longitude: loc.lon,
                totalDwellSeconds: loc.totalDwellSeconds,
                visitCount: loc.visitCount,
                locationType: 'unknown',
                placeName: null,
                address: null,
                neighborhood: null,
                city: null,
                state: null,
                nearbyPlaces: [],
              };
            }
          })
        );
        
        // Add enriched locations to entity data
        entitySpecificData.topDwellLocations = enrichedLocations;
        console.log('[Quick Ask] Enriched dwell locations:', enrichedLocations);
        
      } catch (e) {
        console.log('[Quick Ask] Could not enrich dwell locations:', e);
      }
    }

    // Fetch area intel based on current map view or device locations
    let areaIntel: AreaSummary | null = null;
    try {
      let centerLat: number | null = null;
      let centerLon: number | null = null;
      let radiusMiles = 10;

      // If entity-specific, use entity's location
      if (entitySpecificData && entitySpecificData.latitude && entitySpecificData.longitude) {
        centerLat = entitySpecificData.latitude;
        centerLon = entitySpecificData.longitude;
        radiusMiles = 5; // Smaller radius for entity-specific queries
        console.log(`[Quick Ask] Using entity location: ${centerLat}, ${centerLon}`);
      }
      // Prefer map viewport if available (user's current view)
      else if (mapViewport && mapViewport.latitude && mapViewport.longitude) {
        centerLat = mapViewport.latitude;
        centerLon = mapViewport.longitude;
        // Adjust radius based on zoom level (higher zoom = smaller area)
        radiusMiles = mapViewport.zoom > 14 ? 2 : mapViewport.zoom > 12 ? 5 : mapViewport.zoom > 10 ? 10 : 20;
        console.log(`[Quick Ask] Using map viewport: ${centerLat}, ${centerLon} (zoom: ${mapViewport.zoom}, radius: ${radiusMiles}mi)`);
      } 
      // Fallback to center of visible devices
      else if (devices.length > 0) {
        const lats = devices.filter(d => d.latitude).map(d => d.latitude!);
        const lons = devices.filter(d => d.longitude).map(d => d.longitude!);
        if (lats.length > 0 && lons.length > 0) {
          centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
          console.log(`[Quick Ask] Using device center: ${centerLat}, ${centerLon}`);
        }
      }

      if (centerLat !== null && centerLon !== null) {
        areaIntel = await areaIntelService.getAreaSummary(centerLat, centerLon, radiusMiles);
      }
    } catch (e) {
      console.log('[Quick Ask] Area intel not available:', e);
    }

    // Build area analysis data if applicable
    let areaAnalysisData: {
      isAreaAnalysis: boolean;
      bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number };
      devicesInArea: number;
      dwellsInArea: number;
      uniqueEntitiesInArea: (string | undefined)[];
      entityCount: number;
      devicesByEntity: Record<string, any[]>;
      dwellsByEntity: Record<string, any[]>;
      totalDwellTimeInArea: number;
      activityTimeRange: { earliest: string; latest: string } | null;
    } | null = null;
    if (isAreaAnalysis && areaBounds) {
      // Get unique entities in the area
      const uniqueEntitiesInArea = new Set([
        ...areaDevices.map(d => d.entity_id).filter(Boolean),
        ...areaDwells.map(d => d.entity_id).filter(Boolean),
      ]);
      
      // Detailed dwell info for entities in area
      const dwellsByEntity: Record<string, any[]> = {};
      areaDwells.forEach(d => {
        const entityId = d.entity_id || 'Unknown';
        if (!dwellsByEntity[entityId]) dwellsByEntity[entityId] = [];
        dwellsByEntity[entityId].push({
          deviceId: d.device_id,
          deviceType: d.device_type,
          duration: d.duration,
          durationSeconds: d.duration_seconds,
          startTime: d.start_time,
          endTime: d.end_time,
          latitude: d.latitude,
          longitude: d.longitude,
        });
      });
      
      areaAnalysisData = {
        isAreaAnalysis: true,
        bounds: areaBounds,
        devicesInArea: areaDevices.length,
        dwellsInArea: areaDwells.length,
        uniqueEntitiesInArea: Array.from(uniqueEntitiesInArea),
        entityCount: uniqueEntitiesInArea.size,
        // Device breakdown in area
        devicesByEntity: areaDevices.reduce((acc, d) => {
          const entityId = d.entity_id || 'Unknown';
          if (!acc[entityId]) acc[entityId] = [];
          acc[entityId].push({
            deviceId: d.device_id,
            deviceType: d.device_type,
            entityType: d.entity_type,
            isOnline: (d.last_seen_minutes_ago ?? 999) <= 30,
            lastSeenMinutesAgo: d.last_seen_minutes_ago,
            latitude: d.latitude,
            longitude: d.longitude,
            speed: d.speed,
          });
          return acc;
        }, {} as Record<string, any[]>),
        // Dwell breakdown by entity
        dwellsByEntity,
        // Total dwell time in area
        totalDwellTimeInArea: areaDwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0),
        // Time range of activity in area
        activityTimeRange: areaDwells.length > 0 ? {
          earliest: areaDwells.reduce((min, d) => 
            new Date(d.start_time) < new Date(min.start_time) ? d : min
          ).start_time,
          latest: areaDwells.reduce((max, d) => 
            new Date(d.end_time) > new Date(max.end_time) ? d : max
          ).end_time,
        } : null,
      };
    }

    const context = {
      // IMPORTANT: Dashboard Overview - current state of the Common Operating Picture
      overview: {
        description: `Dashboard showing ${isAreaAnalysis ? areaDevices.length : totalDevices} devices and ${isAreaAnalysis ? areaDwells.length : dwells.length} dwell locations.`,
        dataCounts: {
          totalDevices: isAreaAnalysis ? areaDevices.length : totalDevices,
          activeDevices: isAreaAnalysis ? areaDevices.filter(d => (d.last_seen_minutes_ago ?? 999) <= 30).length : activeDevices,
          dwellLocations: isAreaAnalysis ? areaDwells.length : dwells.length,
          geofences: geofences.length,
          isAreaAnalysisActive: isAreaAnalysis,
        },
        currentFilters: {
          entityType: filters.entityType || 'All',
          isAreaAnalysis,
        },
      },
      totalDevices: isAreaAnalysis ? areaDevices.length : totalDevices,
      activeDevices: isAreaAnalysis ? areaDevices.filter(d => (d.last_seen_minutes_ago ?? 999) <= 30).length : activeDevices,
      devicesInView,
      entityCounts,
      staleDevices,
      recentlyActive,
      sseConnected,
      filters,
      currentTab,
      timeRange: 'Last Week',
      dwellMetrics,
      deviceStatusBreakdown,
      geofenceMetrics,
      entitySpecificData,
      // Entity comparison data (when two entities mentioned)
      comparisonData,
      // Co-location events (different entities at same place same time)
      coLocationEvents: coLocationEvents.slice(0, 10), // Top 10 co-locations
      coLocationMetadata: {
        totalDwellsAnalyzed: dwells.length,
        uniqueEntitiesInDwells: Array.from(new Set(dwells.map(d => d.entity_id).filter(Boolean))).length,
        entitiesWithDwells: Array.from(new Set(dwells.map(d => d.entity_id).filter(Boolean))).slice(0, 10),
        eventsFound: coLocationEvents.length,
        requiresMultipleEntities: true,
        explanation: coLocationEvents.length === 0 
          ? `Co-location detection requires dwells from at least 2 different entities at the same location with overlapping times. Currently only ${Array.from(new Set(dwells.map(d => d.entity_id).filter(Boolean))).length} unique entity(s) have dwell data.`
          : null,
      },
      // Co-traveler analysis (entities that traveled together based on GPS tracks)
      coTravelerData,
      // Area analysis data (when user draws an area on the map)
      areaAnalysisData,
      areaIntel, // Traffic and weather data
      // Current map view for area-based queries
      mapView: mapViewport ? {
        latitude: mapViewport.latitude,
        longitude: mapViewport.longitude,
        zoom: mapViewport.zoom,
      } : null,
      // Location context (address, nearby POIs)
      locationContext: locationContext ? {
        address: locationContext.address,
        neighborhood: locationContext.neighborhood,
        city: locationContext.city,
        state: locationContext.state,
        nearbyPlaces: locationContext.nearbyPlaces?.slice(0, 5).map((p: any) => ({
          name: p.name,
          category: p.category,
          distance: p.distance ? `${Math.round(p.distance)}m` : undefined,
        })),
      } : null,
      // Instructions for the AI
      instructions: `When answering questions about the Dashboard:
        - "devices" refers to tracked entities' devices (phones, vehicles, watches)
        - "dwells" are locations where entities stopped for a period of time
        - "geofences" are user-defined geographic areas for monitoring
        - "co-location" events show when different entities were at the same place/time
        - "co-travelers" are entities that have traveled together
        - Use overview.dataCounts for accurate counts when asked about quantities
        - If an area is drawn on the map (isAreaAnalysisActive=true), data is filtered to that area
        - The map shows real-time positions of active devices`,
    };

    try {
      const response = await fetch('/api/cop-chat/quick-ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
        },
        body: JSON.stringify({
          question: question || quickAskQuestion || null,
          context,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Prepend area context and/or map action message if applicable
        const fullResponse = areaContextPrefix + mapActionPrefix + data.answer;
        setQuickAskResponse(fullResponse);
        setQuickAskSuggestions(data.suggestions || []);
        
        // Add assistant response to chat history
        setChatHistory(prev => [...prev, {
          role: 'assistant',
          content: fullResponse,
          timestamp: new Date(),
        }]);
        
        // Scroll to bottom of chat
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        
        // If area analysis, automatically populate map with dwells and device positions
        if (isAreaAnalysis && onMapAction && (areaDwells.length > 0 || areaDevices.length > 0)) {
          const areaMarkers: MapMarker[] = [];
          
          // Add dwell markers (group by location to avoid clutter)
          const dwellLocations = new Map<string, { dwells: typeof areaDwells; lat: number; lon: number }>();
          areaDwells.forEach(dwell => {
            if (!dwell.latitude || !dwell.longitude) return;
            const locKey = `${dwell.latitude.toFixed(4)},${dwell.longitude.toFixed(4)}`;
            if (!dwellLocations.has(locKey)) {
              dwellLocations.set(locKey, { dwells: [], lat: dwell.latitude, lon: dwell.longitude });
            }
            dwellLocations.get(locKey)!.dwells.push(dwell);
          });
          
          // Create markers for dwell locations
          let dwellIdx = 0;
          Array.from(dwellLocations.entries()).forEach(([_locKey, data]) => {
            const totalTime = data.dwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0);
            const hours = Math.floor(totalTime / 3600);
            const mins = Math.floor((totalTime % 3600) / 60);
            const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
            const entities = Array.from(new Set(data.dwells.map(d => d.entity_id).filter(Boolean))) as string[];
            
            areaMarkers.push({
              id: `area-dwell-${dwellIdx++}`,
              latitude: data.lat,
              longitude: data.lon,
              label: `${data.dwells.length} visit${data.dwells.length > 1 ? 's' : ''}`,
              type: 'dwell',
              color: totalTime > 7200 ? '#f44336' : totalTime > 1800 ? '#FF9800' : '#4CAF50',
              description: `${entities.join(', ')} • Total: ${timeStr}`,
              entities,
            });
          });
          
          // Add current device position markers
          areaDevices.forEach((device, idx) => {
            if (!device.latitude || !device.longitude) return;
            const isOnline = (device.last_seen_minutes_ago ?? 999) <= 30;
            areaMarkers.push({
              id: `area-device-${idx}`,
              latitude: device.latitude,
              longitude: device.longitude,
              label: device.entity_id || device.device_id,
              type: 'poi',
              color: isOnline ? '#2196F3' : '#9E9E9E',
              description: `${device.device_type || 'Device'} • ${isOnline ? 'Online' : `Offline ${device.last_seen_minutes_ago}m ago`}`,
              entities: device.entity_id ? [device.entity_id] : [],
            });
          });
          
          if (areaMarkers.length > 0) {
            onMapAction({ type: 'add_markers', markers: areaMarkers });
            console.log(`[Quick Ask] Added ${areaMarkers.length} area markers (${dwellLocations.size} dwells, ${areaDevices.length} devices)`);
          }
        }
      } else {
        const errorMsg = mapActionPrefix + 'Unable to get AI response. Please try again.';
        setQuickAskResponse(errorMsg);
        setChatHistory(prev => [...prev, {
          role: 'assistant',
          content: errorMsg,
          timestamp: new Date(),
        }]);
      }
    } catch (error) {
      console.error('Quick Ask error:', error);
      const errorMsg = mapActionPrefix + 'Connection error. Please check your network.';
      setQuickAskResponse(errorMsg);
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: errorMsg,
        timestamp: new Date(),
      }]);
    } finally {
      setQuickAskLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // Theme-aware colors
  const colors = useMemo(() => ({
    background: isDark 
      ? 'linear-gradient(135deg, rgba(30, 32, 48, 0.95) 0%, rgba(20, 22, 35, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
    cardBg: isDark ? 'rgba(40, 44, 62, 0.6)' : 'rgba(255, 255, 255, 0.9)',
    border: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    text: isDark ? '#ffffff' : '#1a1a2e',
    textSecondary: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    textTertiary: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
    accent: primaryColor,
    accentRgb: primaryRgb,
  }), [isDark, primaryColor, primaryRgb]);

  // Generate AI insights based on device data
  const generateInsights = useMemo(() => {
    const newInsights: AIInsight[] = [];
    const now = new Date();

    // Keep this summary aligned with the Stats tab Reporting Status gauge.
    const statusTotalDevices = devices.length;
    const recentlyActive = devices.filter(d => (d.last_seen_minutes_ago ?? 999) < 5).length;
    const activeCount = recentlyActive;
    const staleDevices = devices.filter(d => (d.last_seen_minutes_ago ?? 0) > 60);
    const activePercentage = statusTotalDevices > 0 ? (activeCount / statusTotalDevices) * 100 : 0;

    // Entity type breakdown
    const entityCounts = devices.reduce((acc, d) => {
      const type = d.entity_type || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Device type breakdown
    const deviceTypeCounts = devices.reduce((acc, d) => {
      const type = d.device_type || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // 1. Fleet Status Summary
    newInsights.push({
      id: 'fleet-summary',
      type: 'summary',
      priority: activePercentage < 10 ? 'high' : activePercentage < 50 ? 'medium' : 'low',
      title: 'Fleet Status Analysis',
      description: `${activeCount} of ${statusTotalDevices} devices active (${activePercentage.toFixed(1)}%). ${recentlyActive} devices reported in last 5 minutes. ${sseConnected ? 'Real-time updates active.' : 'Polling mode enabled.'}`,
      timestamp: now,
      icon: <GroupsIcon />,
      color: activePercentage < 10 ? '#f44336' : activePercentage < 50 ? '#ff9800' : '#4caf50',
    });

    // 2. Activity Pattern Detection
    if (recentlyActive > 0) {
      const activeEntities = devices
        .filter(d => (d.last_seen_minutes_ago ?? 999) < 5)
        .map(d => d.entity_id || d.device_id)
        .slice(0, 5);
      
      newInsights.push({
        id: 'activity-pattern',
        type: 'pattern',
        priority: 'low',
        title: 'Active Movement Detected',
        description: `${recentlyActive} device${recentlyActive > 1 ? 's' : ''} showing recent activity. Active tracking in progress for real-time position updates.`,
        timestamp: now,
        icon: <MovementIcon />,
        color: '#4caf50',
        entities: activeEntities,
      });
    }

    // 3. Stale Device Alert
    if (staleDevices.length > 0) {
      const staleEntityIds = staleDevices
        .map(d => d.entity_id || d.device_id)
        .slice(0, 5);
      
      newInsights.push({
        id: 'stale-devices',
        type: 'anomaly',
        priority: staleDevices.length > statusTotalDevices * 0.3 ? 'high' : 'medium',
        title: 'Device Connectivity Alert',
        description: `${staleDevices.length} device${staleDevices.length > 1 ? 's' : ''} haven't reported in over 60 minutes. May indicate connectivity issues, device power-off, or coverage gaps.`,
        timestamp: now,
        icon: <WarningIcon />,
        color: '#ff9800',
        entities: staleEntityIds,
      });
    }

    // 4. Entity Distribution Insight
    const entityTypes = Object.entries(entityCounts);
    if (entityTypes.length > 0) {
      const distribution = entityTypes
        .map(([type, count]) => `${type}: ${count}`)
        .join(', ');
      
      newInsights.push({
        id: 'entity-distribution',
        type: 'insight',
        priority: 'low',
        title: 'Entity Composition',
        description: `Current distribution: ${distribution}. Monitoring ${entityTypes.length} entity type${entityTypes.length > 1 ? 's' : ''} across the operational area.`,
        timestamp: now,
        icon: <InsightIcon />,
        color: primaryColor,
      });
    }

    // 5. Device Type Analysis
    const deviceTypes = Object.entries(deviceTypeCounts);
    if (deviceTypes.length > 1) {
      newInsights.push({
        id: 'device-diversity',
        type: 'insight',
        priority: 'low',
        title: 'Multi-Device Coverage',
        description: `Tracking across ${deviceTypes.length} device types: ${deviceTypes.map(([t, c]) => `${t} (${c})`).join(', ')}. Diverse device coverage enhances tracking reliability.`,
        timestamp: now,
        icon: <SpeedIcon />,
        color: '#2196f3',
      });
    }

    // 6. Location Clustering (if we have coordinates)
    const devicesWithLocation = devices.filter(d => d.latitude && d.longitude);
    if (devicesWithLocation.length > 5) {
      // Simple clustering check - are devices concentrated?
      const lats = devicesWithLocation.map(d => d.latitude!);
      const lons = devicesWithLocation.map(d => d.longitude!);
      const latSpread = Math.max(...lats) - Math.min(...lats);
      const lonSpread = Math.max(...lons) - Math.min(...lons);
      
      const isConcentrated = latSpread < 0.5 && lonSpread < 0.5; // ~50km radius
      
      if (isConcentrated) {
        newInsights.push({
          id: 'location-cluster',
          type: 'pattern',
          priority: 'low',
          title: 'Geographic Concentration',
          description: `${devicesWithLocation.length} devices concentrated in primary operational area. Consider expanding coverage or monitoring for congestion.`,
          timestamp: now,
          icon: <LocationIcon />,
          color: '#9c27b0',
        });
      }
    }

    // 7. Trend Analysis
    if (lastUpdated) {
      const updateAge = (now.getTime() - lastUpdated.getTime()) / 1000;
      if (updateAge < 60) {
        newInsights.push({
          id: 'data-freshness',
          type: 'summary',
          priority: 'low',
          title: 'Data Stream Status',
          description: `Last data refresh ${Math.round(updateAge)} seconds ago. ${sseConnected ? 'Server-Sent Events providing real-time updates.' : 'Periodic polling active.'}`,
          timestamp: now,
          icon: <TimelineIcon />,
          color: '#00bcd4',
        });
      }
    }

// 8. Surveillance & Role Player Status
    const srpCount = entityCounts['SRP'] || 0;  // SRP = Surveillance Team
    const rpCount = entityCounts['RP'] || 0;    // RP = Role Player
    if (srpCount > 0 || rpCount > 0) {
      const srpActive = devices.filter(
        d => d.entity_type === 'SRP' && (d.last_seen_minutes_ago ?? 999) <= 30
      ).length;
      const rpActive = devices.filter(
        d => d.entity_type === 'RP' && (d.last_seen_minutes_ago ?? 999) <= 30
      ).length;
      const totalActive = srpActive + rpActive;

      newInsights.push({
        id: 'srp-rp-status',
        type: 'insight',
        priority: totalActive === 0 ? 'medium' : 'low',
        title: 'Support Team Status',
        description: srpCount > 0 && rpCount > 0 
          ? `Surveillance: ${srpActive}/${srpCount} active • Role Players: ${rpActive}/${rpCount} active`
          : srpCount > 0 
            ? `Surveillance Team: ${srpActive} of ${srpCount} active`
            : `Role Players: ${rpActive} of ${rpCount} active`,
        timestamp: now,
        icon: <VisibilityIcon />,
        color: totalActive === 0 ? '#ff9800' : '#4caf50',
      });
    }

    // 9. Dwell Analysis
    if (dwells.length > 0) {
      // Calculate dwell statistics
      const totalDwellTime = dwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0);
      const avgDwellTime = totalDwellTime / dwells.length;
      const maxDwell = dwells.reduce((max, d) => (d.duration_seconds || 0) > (max.duration_seconds || 0) ? d : max, dwells[0]);
      
      // Format durations for display
      const formatDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
      };

      // Count unique entities with dwells
      const uniqueDwellEntities = new Set(dwells.map(d => d.entity_id)).size;

      // Check for long dwells (over 4 hours)
      const longDwells = dwells.filter(d => (d.duration_seconds || 0) > 14400);

      newInsights.push({
        id: 'dwell-summary',
        type: 'insight',
        priority: 'low',
        title: 'Dwell Activity Summary',
        description: `${dwells.length} dwell events recorded across ${uniqueDwellEntities} entities. Average dwell: ${formatDuration(avgDwellTime)}, Total time: ${formatDuration(totalDwellTime)}.`,
        timestamp: now,
        icon: <LocationIcon />,
        color: '#ff9800',
      });

      // Alert for unusually long dwells
      if (longDwells.length > 0) {
        const longestEntity = maxDwell.entity_id || 'Unknown';
        newInsights.push({
          id: 'long-dwell-alert',
          type: 'anomaly',
          priority: longDwells.length > 3 ? 'high' : 'medium',
          title: 'Extended Dwell Detected',
          description: `${longDwells.length} dwell${longDwells.length > 1 ? 's' : ''} exceeding 4 hours. Longest: ${longestEntity} at ${formatDuration(maxDwell.duration_seconds || 0)}. May indicate stationary activity or overnight location.`,
          timestamp: now,
          icon: <WarningIcon />,
          color: '#ff5722',
          entities: longDwells.slice(0, 5).map(d => d.entity_id),
        });
      }

      // Recent dwells (last 24 hours)
      const now24hAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const recentDwells = dwells.filter(d => new Date(d.start_time) > now24hAgo);
      if (recentDwells.length > 0) {
        newInsights.push({
          id: 'recent-dwells',
          type: 'pattern',
          priority: 'low',
          title: 'Recent Dwell Activity',
          description: `${recentDwells.length} dwell events in the last 24 hours. ${recentDwells.length > 10 ? 'High activity detected.' : 'Normal activity levels.'}`,
          timestamp: now,
          icon: <TrendIcon />,
          color: '#8bc34a',
        });
      }
    }

    // 10. Device Status Insight
    const movingDevices = devices.filter(d => (d.speed ?? 0) > 0.5);
    if (movingDevices.length > 0) {
      newInsights.push({
        id: 'moving-devices',
        type: 'pattern',
        priority: movingDevices.length > 5 ? 'medium' : 'low',
        title: 'Active Movement',
        description: `${movingDevices.length} device${movingDevices.length > 1 ? 's' : ''} currently in motion. Real-time tracking active for moving targets.`,
        timestamp: now,
        icon: <MovementIcon />,
        color: '#2196f3',
        entities: movingDevices.slice(0, 5).map(d => d.entity_id || d.device_id),
      });
    }

    // 11. Geofence Coverage Insight
    if (geofences.length > 0) {
      const activeGeofences = geofences.filter(g => g.is_active !== false);
      newInsights.push({
        id: 'geofence-coverage',
        type: 'insight',
        priority: 'low',
        title: 'Geofence Coverage',
        description: `${activeGeofences.length} active geofence${activeGeofences.length !== 1 ? 's' : ''} monitoring the operational area. ${geofences.length > activeGeofences.length ? `(${geofences.length - activeGeofences.length} inactive)` : 'All geofences active.'}`,
        timestamp: now,
        icon: <LocationIcon />,
        color: '#9c27b0',
      });
    }

    return newInsights;
  }, [devices, totalDevices, activeDevices, lastUpdated, sseConnected, primaryColor, dwells, geofences]);

  // Throttle insight updates to reduce CPU usage (30 second minimum between auto-updates)
  const INSIGHT_UPDATE_INTERVAL = 30000; // 30 seconds
  const lastInsightUpdate = useRef<number>(0);
  const pendingUpdate = useRef<NodeJS.Timeout | null>(null);
  const generateInsightsRef = useRef(generateInsights);
  generateInsightsRef.current = generateInsights;

  // Set insights on first render immediately
  useEffect(() => {
    setIsAnalyzing(true);
    const timer = setTimeout(() => {
      setInsights(generateInsightsRef.current);
      setLastAnalysis(new Date());
      setIsAnalyzing(false);
      lastInsightUpdate.current = Date.now();
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Throttled updates when data changes (skips first render handled above)
  useEffect(() => {
    if (lastInsightUpdate.current === 0) return;

    if (pendingUpdate.current) {
      clearTimeout(pendingUpdate.current);
    }

    const timeSinceLastUpdate = Date.now() - lastInsightUpdate.current;
    const delay = Math.max(0, INSIGHT_UPDATE_INTERVAL - timeSinceLastUpdate);

    pendingUpdate.current = setTimeout(() => {
      setInsights(generateInsightsRef.current);
      setLastAnalysis(new Date());
      lastInsightUpdate.current = Date.now();
      pendingUpdate.current = null;
    }, delay);

    return () => {
      if (pendingUpdate.current) {
        clearTimeout(pendingUpdate.current);
        pendingUpdate.current = null;
      }
    };
  }, [generateInsights]);

  // Force refresh function (bypasses throttle)
  const forceRefreshInsights = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setInsights(generateInsightsRef.current);
      setLastAnalysis(new Date());
      setIsAnalyzing(false);
      lastInsightUpdate.current = Date.now();
    }, 300);
  };

  // Mark all current insights as seen when the Events tab is active
  useEffect(() => {
    if (currentTab === 'Events' && insights.length > 0) {
      setSeenInsightIds(new Set(insights.map(i => i.id)));
    }
  }, [currentTab, insights]);

  // Count unseen insights
  const unseenCount = insights.filter(i => !seenInsightIds.has(i.id)).length;

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      default: return '#4caf50';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'pattern': return 'Pattern';
      case 'anomaly': return 'Anomaly';
      case 'alert': return 'Alert';
      case 'insight': return 'Insight';
      case 'summary': return 'Summary';
      default: return type;
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        pb: 1.5,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: `linear-gradient(135deg, rgba(${colors.accentRgb}, 0.2), rgba(${colors.accentRgb}, 0.1))`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid rgba(${colors.accentRgb}, 0.3)`,
          }}>
            <AnalyticsIcon sx={{ color: colors.accent, fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ 
              color: colors.text, 
              fontWeight: 'bold', 
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}>
              Smart Analytics
              {isAnalyzing && (
                <Box component="span" sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: colors.accent,
                  animation: 'pulse 1s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }} />
              )}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography sx={{ color: colors.textTertiary, fontSize: '0.65rem' }}>
                {insights.length} insights
              </Typography>
              {['high', 'medium', 'low'].map(priority => {
                const count = insights.filter(i => i.priority === priority).length;
                return count > 0 ? (
                  <Chip
                    key={priority}
                    label={`${count}`}
                    size="small"
                    sx={{
                      height: 14,
                      fontSize: '0.55rem',
                      bgcolor: `${getPriorityColor(priority)}20`,
                      color: getPriorityColor(priority),
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                ) : null;
              })}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Badge
            badgeContent={unseenCount}
            color="error"
            max={9}
          >
            <Tooltip title={unseenCount > 0 ? `${unseenCount} new insight${unseenCount !== 1 ? 's' : ''} - click to refresh` : 'Refresh Analysis (auto-updates every 30s)'}>
              <IconButton
                size="small"
                onClick={forceRefreshInsights}
                sx={{ color: colors.textSecondary }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Badge>
        </Box>
      </Box>

      {/* Loading indicator */}
      {isAnalyzing && (
        <LinearProgress 
          sx={{ 
            mb: 1, 
            borderRadius: 1,
            bgcolor: `rgba(${colors.accentRgb}, 0.1)`,
            '& .MuiLinearProgress-bar': {
              bgcolor: colors.accent,
            },
          }} 
        />
      )}

      {/* Ask AI Section - At top for easy access */}
      <Box sx={{ 
        mb: 2, 
        p: 1.5, 
        bgcolor: isDark ? 'rgba(30, 32, 48, 0.6)' : 'rgba(248, 250, 252, 0.9)',
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AskIcon sx={{ color: colors.accent, fontSize: '1rem' }} />
          <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.8rem' }}>
            Ask Sentinel
          </Typography>

          {/* Chat history indicator - show when there's history but not in chat mode */}
          {!isChatMode && chatHistory.length > 0 && (
            <Chip
              label={`Chat: ${chatHistory.length}`}
              size="small"
              onClick={() => setIsChatMode(true)}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: `rgba(${colors.accentRgb}, 0.15)`,
                color: colors.accent,
                cursor: 'pointer',
                '&:hover': { bgcolor: `rgba(${colors.accentRgb}, 0.25)` },
              }}
            />
          )}

          {/* Area Analysis Button */}
          {onMapAction && (
            <Tooltip title={isDrawingAnalysisArea ? "Drawing area... Click to cancel" : analysisArea ? "Clear selected area" : "Draw area on map to analyze"}>
              <IconButton
                size="small"
                onClick={() => {
                  if (isDrawingAnalysisArea) {
                    onMapAction({ type: 'cancel_area_analysis' });
                  } else if (analysisArea) {
                    onMapAction({ type: 'cancel_area_analysis' });
                  } else {
                    onMapAction({ type: 'start_area_analysis' });
                  }
                }}
                sx={{
                  ml: 'auto',
                  color: isDrawingAnalysisArea ? '#FF9800' : analysisArea ? '#4CAF50' : colors.textSecondary,
                  bgcolor: isDrawingAnalysisArea ? 'rgba(255, 152, 0, 0.1)' : analysisArea ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                  '&:hover': {
                    bgcolor: isDrawingAnalysisArea ? 'rgba(255, 152, 0, 0.2)' : analysisArea ? 'rgba(76, 175, 80, 0.2)' : `rgba(${colors.accentRgb}, 0.1)`,
                  },
                  p: 0.5,
                  animation: isDrawingAnalysisArea ? 'pulse 1.5s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              >
                {analysisArea ? <CheckIcon sx={{ fontSize: '1rem' }} /> : <AreaSelectIcon sx={{ fontSize: '1rem' }} />}
              </IconButton>
            </Tooltip>
          )}
          
          {/* Export & Clear buttons */}
          {chatHistory.length > 0 && (
            <Tooltip title="Export to PDF (with map & timeline)">
              <IconButton 
                size="small" 
                onClick={async () => {
                  await generateSentinelChatPDF(
                    {
                      title: 'COP Intelligence Report',
                      subtitle: 'Common Operating Picture Analysis',
                      messages: chatHistory.map(m => ({
                        role: m.role,
                        content: m.content,
                        timestamp: m.timestamp,
                      })),
                      additionalContext: `Analysis Context: ${devices.length} devices, ${dwells.length} dwell locations, ${geofences.length} geofences`,
                      classification: 'Controlled Unclassified Information (CUI)',
                    },
                    'COP_Intelligence_Report.pdf',
                    { mapElementId: 'cop-map-container', timelineElementId: 'cop-timeline-widget' }
                  );
                }}
                sx={{ ml: onMapAction ? 0 : 'auto', color: colors.textSecondary, p: 0.25 }}
              >
                <PdfIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          )}
          
          {(quickAskResponse || chatHistory.length > 0) && !analysisArea && (
            <Tooltip title={chatHistory.length > 0 ? "Clear chat" : "Clear response"}>
              <IconButton 
                size="small" 
                onClick={() => {
                  setQuickAskResponse(null);
                  setQuickAskQuestion('');
                  setChatHistory([]);
                  setIsChatMode(false);
                }}
                sx={{ color: colors.textSecondary, p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Area Analysis Status */}
        {(isDrawingAnalysisArea || analysisArea) && (
          <Box sx={{
            mb: 1,
            p: 1,
            bgcolor: isDrawingAnalysisArea ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)',
            borderRadius: 1,
            border: `1px solid ${isDrawingAnalysisArea ? '#FF9800' : '#4CAF50'}`,
          }}>
            {isDrawingAnalysisArea ? (
              <Typography sx={{ fontSize: '0.75rem', color: '#FF9800', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AreaSelectIcon sx={{ fontSize: '0.9rem' }} />
                Draw a polygon on the map to select area for analysis...
              </Typography>
            ) : analysisArea && (
              <>
                <Typography sx={{ fontSize: '0.75rem', color: '#4CAF50', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <CheckIcon sx={{ fontSize: '0.9rem' }} />
                  Area selected! Found in this area:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip 
                    label={`${filterDevicesInArea(analysisArea).length} devices`} 
                    size="small" 
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4CAF50' }} 
                  />
                  <Chip 
                    label={`${filterDwellsInArea(analysisArea).length} dwells`} 
                    size="small" 
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4CAF50' }} 
                  />
                  {onMapAction && (
                    <Chip
                      icon={<CloseIcon sx={{ fontSize: '0.8rem !important', color: '#FF9800 !important' }} />}
                      label="Clear Area"
                      size="small"
                      onClick={() => onMapAction({ type: 'cancel_area_analysis' })}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: 'rgba(255, 152, 0, 0.12)',
                        color: '#FF9800',
                        border: '1px solid rgba(255, 152, 0, 0.35)',
                        cursor: 'pointer',
                        '& .MuiChip-label': { px: 0.75 },
                        '& .MuiChip-icon': { ml: 0.5 },
                        '&:hover': {
                          bgcolor: 'rgba(255, 152, 0, 0.22)',
                        },
                      }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.65rem', color: colors.textSecondary, mt: 0.5 }}>
                  Ask a question like "What happened here?" or "Who visited this area?"
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Question Input */}
        <TextField
          fullWidth
          size="small"
          placeholder={onMapAction ? "Ask anything or 'zoom to [entity]'..." : "Ask about fleet status, dwells, alerts..."}
          value={quickAskQuestion}
          onChange={(e) => setQuickAskQuestion(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !quickAskLoading) {
              handleQuickAsk();
            }
          }}
          disabled={quickAskLoading}
          InputProps={{
            sx: {
              bgcolor: isDark ? 'rgba(20, 22, 35, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              color: colors.text,
              fontSize: '0.8rem',
              '& fieldset': { borderColor: colors.border },
              '&:hover fieldset': { borderColor: colors.accent },
              '&.Mui-focused fieldset': { borderColor: colors.accent },
            },
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* Prompt Library Button */}
                <Tooltip title="Prompt Library">
                  <IconButton
                    size="small"
                    onClick={(e) => setPromptMenuAnchor(e.currentTarget)}
                    sx={{ 
                      color: promptMenuOpen ? colors.accent : colors.textSecondary,
                      '&:hover': { color: colors.accent },
                    }}
                  >
                    <PromptIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
                {/* Send Button */}
              <IconButton
                size="small"
                onClick={() => handleQuickAsk()}
                disabled={quickAskLoading}
                sx={{ color: colors.accent }}
              >
                {quickAskLoading ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: '1rem' }} />}
              </IconButton>
              </Box>
            ),
          }}
        />

        {/* Prompt Library Menu */}
        <Menu
          anchorEl={promptMenuAnchor}
          open={promptMenuOpen}
          onClose={() => setPromptMenuAnchor(null)}
          PaperProps={{
            sx: {
              bgcolor: isDark ? 'rgba(30, 32, 48, 0.98)' : 'rgba(255, 255, 255, 0.98)',
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              maxHeight: 400,
              minWidth: 280,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }
          }}
        >
          <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${colors.border}` }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.text }}>
              Analysis Prompts
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: colors.textSecondary }}>
              Click to auto-fill the chat input
            </Typography>
          </Box>
          {predefinedPrompts.map((category, catIdx) => (
            <Box key={catIdx}>
              <Box sx={{ px: 2, py: 0.75, bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)' }}>
                <Typography sx={{ 
                  fontSize: '0.7rem', 
                  fontWeight: 600, 
                  color: colors.accent,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}>
                  {category.icon}
                  {category.category}
                </Typography>
              </Box>
              {category.prompts.map((item, idx) => (
                <MenuItem
                  key={idx}
                  onClick={() => {
                    setQuickAskQuestion(item.prompt);
                    setPromptMenuAnchor(null);
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    color: colors.text,
                    py: 0.75,
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </MenuItem>
              ))}
            </Box>
          ))}
        </Menu>

        {/* Loading State - only show when not in chat mode */}
        {quickAskLoading && !isChatMode && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
              Analyzing...
            </Typography>
          </Box>
        )}

        {/* Response - only show latest when not in chat mode (chat mode shows history below) */}
        {quickAskResponse && !quickAskLoading && !isChatMode && (
          <Box sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: isDark ? 'rgba(20, 22, 35, 0.6)' : 'rgba(255, 255, 255, 0.9)',
            borderRadius: 1.5,
            border: `1px solid ${colors.border}`,
            maxHeight: { xs: 150, md: 200 },
            overflow: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
              borderRadius: 4,
            },
          }}>
            <Typography 
              sx={{ 
                color: colors.text, 
                fontSize: '0.8rem',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                '& strong': { color: colors.accent },
              }}
              dangerouslySetInnerHTML={{ 
                __html: quickAskResponse
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n/g, '<br/>') 
              }}
            />
            
            {/* Suggestions */}
            {quickAskSuggestions.length > 0 && (
              <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${colors.border}` }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {quickAskSuggestions.slice(0, 3).map((suggestion, idx) => (
                    <Chip
                      key={idx}
                      label={suggestion}
                      size="small"
                      onClick={() => {
                        setQuickAskQuestion(suggestion);
                        handleQuickAsk(suggestion);
                      }}
                      sx={{
                        fontSize: '0.6rem',
                        height: 20,
                        cursor: 'pointer',
                        bgcolor: `rgba(${colors.accentRgb}, 0.1)`,
                        color: colors.accent,
                        '&:hover': { bgcolor: `rgba(${colors.accentRgb}, 0.2)` },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

{/* Scrollable Content Area - Chat History OR Insights */}
      <Box sx={{
        flex: 1,
        minHeight: 0, // Important for flexbox scrolling
        overflowY: 'auto',
        pr: 0.5,
        // Minimalistic scrollbar
        '&::-webkit-scrollbar': { 
          width: 4,
        },
        '&::-webkit-scrollbar-track': { 
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
          borderRadius: 4,
          '&:hover': {
            background: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
          },
        },
        scrollbarWidth: 'thin',
        scrollbarColor: isDark 
          ? 'rgba(255, 255, 255, 0.15) transparent' 
          : 'rgba(0, 0, 0, 0.15) transparent',
      }}>
        {/* Chat Mode - Show conversation history */}
        {isChatMode ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Back to Insights button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pb: 1,
              borderBottom: `1px solid ${colors.border}`,
            }}>
              <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                Chat Session ({chatHistory.length} messages)
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Clear chat history">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setChatHistory([]);
                      setQuickAskResponse(null);
                    }}
                    sx={{ color: colors.textSecondary, p: 0.5 }}
                  >
                    <CloseIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
                <Chip
                  label="Show Insights"
                  size="small"
                  onClick={() => setIsChatMode(false)}
                  sx={{
                    height: 22,
                    fontSize: '0.65rem',
                    bgcolor: `rgba(${colors.accentRgb}, 0.1)`,
                    color: colors.accent,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: `rgba(${colors.accentRgb}, 0.2)` },
                  }}
                />
              </Box>
            </Box>
            
            {/* Chat messages */}
            {chatHistory.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: msg.role === 'user' 
                    ? `rgba(${colors.accentRgb}, 0.1)`
                    : isDark ? 'rgba(40, 44, 62, 0.6)' : 'rgba(248, 250, 252, 0.9)',
                  border: `1px solid ${msg.role === 'user' ? colors.accent : colors.border}`,
                  borderLeft: msg.role === 'user' ? `3px solid ${colors.accent}` : undefined,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ 
                    fontSize: '0.7rem', 
                    fontWeight: 600,
                    color: msg.role === 'user' ? colors.accent : colors.text,
                  }}>
                    {msg.role === 'user' ? 'You' : 'Sentinel'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: colors.textTertiary }}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
                <Typography 
                  sx={{ 
                    fontSize: '0.8rem', 
                    color: colors.text,
                    whiteSpace: 'pre-wrap',
                    '& a': { color: colors.accent },
                    '& strong': { fontWeight: 600 },
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: msg.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br/>')
                  }}
                />
              </Box>
            ))}
            
            {/* Loading indicator */}
            {quickAskLoading && (
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2,
                bgcolor: isDark ? 'rgba(40, 44, 62, 0.6)' : 'rgba(248, 250, 252, 0.9)',
                border: `1px solid ${colors.border}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} />
                  <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                    Analyzing...
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Scroll anchor */}
            <div ref={chatEndRef} />
          </Box>
        ) : insights.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            color: colors.textSecondary,
          }}>
            <AnalyticsIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">
              Analyzing data...
            </Typography>
          </Box>
        ) : (
          insights.map((insight, index) => (
            <Paper
              key={insight.id}
              elevation={0}
              sx={{
                mb: 1.5,
                p: 1.5,
                background: colors.cardBg,
                border: `1px solid ${colors.border}`,
                borderLeft: `3px solid ${insight.color}`,
                borderRadius: 2,
                transition: 'all 0.2s',
                animation: `slideIn 0.3s ease-out ${index * 0.05}s both`,
                '@keyframes slideIn': {
                  from: { opacity: 0, transform: 'translateX(-10px)' },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
                '&:hover': {
                  background: isDark 
                    ? 'rgba(50, 54, 72, 0.8)' 
                    : 'rgba(255, 255, 255, 1)',
                  transform: 'translateX(2px)',
                  boxShadow: isDark 
                    ? `0 4px 12px rgba(0,0,0,0.3)` 
                    : `0 4px 12px rgba(0,0,0,0.1)`,
                },
              }}
            >
              {/* Insight Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1,
                cursor: 'pointer',
              }}
              onClick={() => toggleExpand(insight.id)}
              >
                <Box sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '6px',
                  bgcolor: `${insight.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {React.cloneElement(insight.icon as React.ReactElement, {
                    sx: { color: insight.color, fontSize: 16 }
                  })}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ 
                      color: colors.text, 
                      fontWeight: 600, 
                      fontSize: '0.8rem',
                      flex: 1,
                    }}>
                      {insight.title}
                    </Typography>
                    <Chip
                      label={getTypeLabel(insight.type)}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        bgcolor: `${insight.color}20`,
                        color: insight.color,
                        border: `1px solid ${insight.color}40`,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </Box>
                  <Typography sx={{ 
                    color: colors.textSecondary, 
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                  }}>
                    {insight.description}
                  </Typography>
                </Box>
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: colors.textTertiary,
                    p: 0.5,
                  }}
                >
                  {expandedIds.has(insight.id) ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                </IconButton>
              </Box>

              {/* Expanded Details */}
              <Collapse in={expandedIds.has(insight.id)}>
                <Box sx={{ 
                  mt: 1.5, 
                  pt: 1.5, 
                  borderTop: `1px dashed ${colors.border}`,
                }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      label={`Priority: ${insight.priority.toUpperCase()}`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: `${getPriorityColor(insight.priority)}20`,
                        color: getPriorityColor(insight.priority),
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                    <Chip
                      label={insight.timestamp.toLocaleTimeString()}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: colors.cardBg,
                        color: colors.textSecondary,
                        border: `1px solid ${colors.border}`,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </Box>
                  
                  {insight.entities && insight.entities.length > 0 && (
                    <Box>
                      <Typography sx={{ 
                        color: colors.textTertiary, 
                        fontSize: '0.65rem',
                        mb: 0.5,
                      }}>
                        Related Entities:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {insight.entities.map((entity, i) => (
                          <Chip
                            key={i}
                            label={entity}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              bgcolor: `rgba(${colors.accentRgb}, 0.1)`,
                              color: colors.accent,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        ))}
                        {insight.entities.length === 5 && (
                          <Typography sx={{ 
                            color: colors.textTertiary, 
                            fontSize: '0.6rem',
                            alignSelf: 'center',
                          }}>
                            +more
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))
        )}
      </Box>
    </Box>
  );
};

export default SmartAnalytics;
export type { MapAction, MapMarker, AreaGeometry, TrackPoint, EntityRoute };
